import { revisionedRead } from './readCache';
import { markRequest } from '../lib/actionFailureSignals';
import { jobAddressLines, type JobAddress } from '../utils/jobAddress';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { QueryKey } from '@tanstack/react-query';
import { apiFetch } from '../api/http';
import { fetchEventLines, type EventLineRecord } from './eventLines';
import { scheduleColorTakenError } from './scheduleColors';
import { getSupabaseClient } from './supabase';
import { flattenRichText } from '../../../shared/richTextFlatten.js';
import { hydrateShiftBreaks, normalizeUnpaidBreaks, breakClassification, type UnpaidBreak, type BreakClassification, type DailyBreak } from './scheduleBreaks';

type ClientOption = { supabase?: SupabaseClient };

type LaborCalendarWindowFetchOptions = ClientOption & {
  hydrateEventDetails?: boolean;
  filterLogisticsRequirement?: boolean;
  limitedFreelancerView?: boolean;
};

export type LaborCalendarScope = 'team' | 'mine' | 'open' | 'offers' | 'requests' | 'logistics';
export type LaborCalendarPublishStatus = 'all' | 'draft' | 'published';
export type LaborScheduleType = 'labor' | 'pick' | 'return' | 'delivery' | 'pickup' | 'service' | 'other';
export type LaborScheduleSource = 'manual' | 'event_labor' | 'event_logistics' | 'service_ticket' | 'installation' | 'time_tracking';
export type LaborScheduleRecurrenceRule = {
  frequency: 'day' | 'week' | 'month' | 'year';
  interval: number;
  weekdays: number[];
  untilDate: string;
  anchorDate: string;
  timeZone: string;
};
export type LaborCalendarAssignmentStatus =
  | 'all'
  | 'assigned'
  | 'offered'
  | 'accepted'
  | 'declined'
  | 'requested'
  | 'cancelled'
  | 'no_show'
  | 'withdrawn';

export type LaborScheduleAssignment = {
  assignmentId: string;
  profileId: string | null;
  freelancerId: string | null;
  assignmentType: 'employee' | 'freelancer';
  status: Exclude<LaborCalendarAssignmentStatus, 'all'>;
  costRate: number | null;
  displayName: string;
  email: string | null;
};

export type LaborScheduleHistoryEntry = {
  historyId: string;
  shiftId: string;
  assignmentId: string | null;
  entityType: 'shift' | 'assignment' | 'vehicle_assignment' | 'event_link' | 'legacy_activity';
  operation: 'insert' | 'update' | 'delete' | 'activity';
  activityType: string;
  actorProfileId: string | null;
  actorName: string;
  actorEmail: string | null;
  subjectLabel: string | null;
  beforeData: Record<string, unknown> | null;
  afterData: Record<string, unknown> | null;
  changedFields: string[];
  transactionId: string | null;
  occurredAt: string;
  isLegacy: boolean;
};

/** A history entry in Schedule › History, with the shift it belongs to (its last state if deleted). */
export type LaborScheduleViewHistoryEntry = LaborScheduleHistoryEntry & {
  shiftDeleted: boolean;
  shiftStartsAt: string | null;
  shiftEndsAt: string | null;
  shiftJobLabel: string | null;
  shiftRoleName: string | null;
  shiftLocationName: string | null;
};

/** The Schedule view whose history is read: the shifts shown, plus what finds those deleted from it. */
export type LaborScheduleViewHistoryRequest = {
  shiftIds: string[];
  windowStart: string;
  windowEnd: string;
  /** Empty: any location. */
  locationIds: string[];
  eventIds: string[];
  roleIds: string[];
  serviceTicketId: string | null;
};

/** What a History row's change was, for the Change column's filter. */
export type LaborScheduleHistoryChangeKind =
  | 'created' | 'edited' | 'moved' | 'published' | 'deleted' | 'staffing' | 'vehicle' | 'event';
export type LaborScheduleViewHistoryScope = 'all' | 'live' | 'deleted';
export type LaborScheduleViewHistorySort = 'when' | 'change' | 'shift' | 'location' | 'actor';

/** The view plus the table's search, filters and sort. Empty lists mean no filter. */
export type LaborScheduleViewHistoryQuery = LaborScheduleViewHistoryRequest & {
  scope: LaborScheduleViewHistoryScope;
  search: string;
  changeKinds: LaborScheduleHistoryChangeKind[];
  /** A profile id, or 'system' for changes no person made. */
  actorKeys: string[];
  filterLocationIds: string[];
  /** ISO timestamps; from is inclusive, to is exclusive. */
  changedFrom: string | null;
  changedTo: string | null;
  sort: LaborScheduleViewHistorySort;
  sortDesc: boolean;
};

export type LaborScheduleViewHistoryRow = LaborScheduleViewHistoryEntry & {
  shiftLocationId: string | null;
  changeKind: LaborScheduleHistoryChangeKind;
  actorKey: string;
};

export type LaborScheduleViewHistoryFacet = { value: string; label: string; count: number };
export type LaborScheduleViewHistoryFacets = {
  kind: LaborScheduleViewHistoryFacet[];
  actor: LaborScheduleViewHistoryFacet[];
  location: LaborScheduleViewHistoryFacet[];
};

export type LaborScheduleVehicleAssignment = {
  assignmentId: string;
  shiftId: string;
  vehicleId: string;
  status: 'assigned' | 'cancelled' | 'removed';
  loadStartsAt: string | null;
  returnAt: string | null;
  driverProfileId: string | null;
  driverFreelancerId: string | null;
  driverName: string | null;
  driverEmail: string | null;
  transitMinutes: number | null;
  eventDriveMinutes: number | null;
  effectiveTransitMinutes: number | null;
  displayName: string;
  vehicleType: string | null;
  licensePlate: string | null;
  capacity: string | null;
  locationId: string | null;
  locationName: string | null;
  notes: string | null;
};

export type LaborScheduleEventHoverDetails = {
  salesPersonName?: string | null;
  customerCompanyName: string | null;
  clientName: string | null;
  grandTotal: number | null;
  paymentTerms: string | null;
  equipmentSummary: string[];
  venueAddress: string | null;
};

export type LaborScheduleEventLink = {
  id: string;
  name: string;
  eventCode: string | null;
  siteLocationId: string | null;
  eventVenueId: string | null;
  venueId: string | null;
  venueName: string | null;
  laborCalendarColor: string | null;
  startDate: string | null;
  endDate: string | null;
} & LaborScheduleEventHoverDetails;

export type LaborScheduleJobSite = {
  id: string;
  label: string;
  address: string | null;
  addressParts?: JobAddress;
  venueId?: string | null;
  locationName?: string | null;
};

export type LaborScheduleShift = {
  /** Exact room venue, or every labeled venue when a shift has no room. */
  jobSites?: LaborScheduleJobSite[];
  /** Job's ITA Location/logistics origin; independent work uses the shift's site. */
  jobLocationId?: string | null;
  jobLocationName?: string | null;
  locationAddress?: string | null;
  locationVenues?: LaborScheduleJobSite[];
  id: string;
  eventId: string | null;
  eventName: string;
  eventCode: string | null;
  events: LaborScheduleEventLink[];
  eventRoomId: string | null;
  roomName: string | null;
  laborRoleId: string | null;
  roleName: string | null;
  siteLocationId: string | null;
  locationName: string | null;
  startsAt: string;
  endsAt: string | null;
  updatedAt: string | null;
  requiredPeople: number;
  scheduleStatus: 'planned' | 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  publishStatus: 'draft' | 'published';
  scheduleType: LaborScheduleType;
  source: LaborScheduleSource;
  isOpen: boolean;
  /** A shift managers think will happen: it sits in the Tentative row while it needs people. */
  isTentative: boolean;
  needsTime: boolean;
  shiftLabel?: string | null;
  unpaidBreakMinutes?: number | null;
  unpaidBreaks?: UnpaidBreak[] | null;
  lunchBreakMinutes?: number | null;
  lunchBreakClassification?: BreakClassification;
  dailyBreaks?: DailyBreak[];
  slingUnpaidBreakMinutes?: number | null;
  recurrenceSeriesId?: string | null;
  recurrenceRule?: LaborScheduleRecurrenceRule | null;
  recurrenceIndex?: number | null;
  notes: string | null;
  assignmentCount: number;
  shortage: number;
  conflictCount: number;
  assignments: LaborScheduleAssignment[];
  vehicleAssignments: LaborScheduleVehicleAssignment[];
  serviceTicketId?: string | null;
  serviceTicketLaborLineId?: string | null;
  installationJobId?: string | null;
  /** The linked installation's status, read with its job details. */
  installationJobStatus?: string | null;
  installationLaborLineId?: string | null;
  installationRoomId?: string | null;
  /** Set when the shift holds recorded time; the planned times are what it replaced. */
  timeEntryId?: string | null;
  /** A read-only presentation of an unmatched open ledger entry; no planned row exists. */
  openActual?: boolean;
  actualTimezone?: string;
  plannedStartsAt?: string | null;
  plannedEndsAt?: string | null;
  /** This shift's own Schedule color; wins over its event, installation or ticket color. */
  calendarColor?: string | null;
  /** Colors the linked installation job or service ticket gives all its shifts. */
  installationCalendarColor?: string | null;
  serviceTicketCalendarColor?: string | null;
  jobDetails?: {
    customerCompanyName: string | null;
    locationName: string | null;
    address: string | null;
    addressParts?: JobAddress;
    itaLocationId?: string | null;
    /** Set only when an Installation's location is a saved venue, so it can share that venue's color. */
    venueId: string | null;
  };
};

export type LaborScheduleMove = {
  shiftId: string;
  startsAt: string;
  endsAt?: string | null;
  resourceId?: string | null;
  fromResourceId?: string | null;
  expectedUpdatedAt: string;
  shift?: LogisticsWarehouseShift | null;
  notifyAssignment?: boolean;
};

export type LaborScheduleMoveResult = {
  shiftId: string;
  updatedAt: string;
  state: Pick<LaborScheduleShift, 'startsAt' | 'endsAt' | 'isOpen' | 'isTentative' | 'needsTime'
    | 'requiredPeople' | 'scheduleStatus' | 'publishStatus' | 'assignments' | 'assignmentCount' | 'shortage'>;
};

export type LaborScheduleConflict = {
  shiftId: string;
  expectedUpdatedAt: string | null;
  actualUpdatedAt: string | null;
};

export class LaborScheduleConflictError extends Error {
  readonly conflicts: LaborScheduleConflict[];
  readonly reloadRequired: boolean;

  constructor(conflicts: LaborScheduleConflict[], reloadRequired = false) {
    super(reloadRequired
      ? 'The original assignment changed. Reload the schedule before moving it.'
      : 'This shift changed after you opened the schedule.');
    this.name = 'LaborScheduleConflictError';
    this.conflicts = conflicts;
    this.reloadRequired = reloadRequired;
  }
}

export type LaborScheduleBroadcastPayload = {
  table?: string;
  operation?: string;
  shift_id?: string | null;
  event_id?: string | null;
  old_event_id?: string | null;
  site_location_id?: string | null;
  old_site_location_id?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  old_starts_at?: string | null;
  old_ends_at?: string | null;
};

export type LaborScheduleBroadcastWindow = {
  windowStart: string;
  windowEnd: string;
  locationIds?: string[];
  eventIds?: string[];
};

type LogisticsWarehouseShift = Pick<LaborScheduleShift, 'id' | 'eventId' | 'events' | 'scheduleType' | 'source' | 'startsAt'>;

export type LaborScheduleEventWarehousePatch = Partial<{
  fill_date: string;
  fill_time: string;
  ship_date: string;
  ship_time: string;
  load_out_date: string;
  load_out_time: string;
  return_date: string;
  return_time: string;
}>;

export type LaborScheduleEventVehicleTrip = LaborScheduleVehicleAssignment & {
  eventId: string | null;
  eventName: string | null;
  eventCode: string | null;
  scheduleType: LaborScheduleType;
  roleName: string | null;
  startsAt: string | null;
  endsAt: string | null;
};

export type LaborScheduleResource = {
  id: string;
  title: string;
  personId: string | null;
  kind: 'lane' | 'employee' | 'freelancer' | 'vehicle';
  detail: string | null;
  homeLocationId: string | null;
  assignedLocationIds: string[];
  rolePreferenceIds: string[];
  roleAssignmentCounts?: Record<string, number>;
  vehicleId?: string | null;
  vehicleType?: string | null;
  licensePlate?: string | null;
  capacity?: string | null;
  inactive: boolean;
  hiddenFromSchedule: boolean;
  unpaidLunchMinutes?: 0 | 30 | 60;
};

export type LaborScheduleRoleAssignmentCountMap = Record<string, Record<string, number>>;

export type LaborCalendarFilters = {
  windowStart: string;
  windowEnd: string;
  scope: LaborCalendarScope;
  locationIds?: string[] | null;
  eventId?: string | null;
  eventIds?: string[] | null;
  roleId?: string | null;
  roleIds?: string[] | null;
  personProfileId?: string | null;
  personProfileIds?: string[] | null;
  freelancerId?: string | null;
  freelancerIds?: string[] | null;
  publishStatus?: LaborCalendarPublishStatus | null;
  assignmentStatus?: LaborCalendarAssignmentStatus | null;
  myEventsOnly?: boolean | null;
  includeUnconfirmedEvents?: boolean | null;
  revenueDateBasis?: LaborCalendarRevenueDateBasis | null;
};

export type LaborCalendarRevenueDateBasis = 'start' | 'end';
export type LaborCalendarGuestLinkView = 'any' | 'week' | 'day' | 'month' | 'list';
export type LaborCalendarGuestLinkGroup = 'person' | 'location';
export type LaborCalendarGuestLinkColorMode = 'event' | 'location';
export type LaborCalendarGuestLinkResourceGroupFilter = 'all' | 'assigned' | 'unassigned' | 'people' | 'lanes';
export type LaborCalendarGuestLinkResourceSort = 'name' | 'name-desc' | 'shift-count' | 'first-shift';
export type LaborCalendarGuestLinkFilters = {
  scope: Extract<LaborCalendarScope, 'team' | 'open' | 'logistics'>;
  view: LaborCalendarGuestLinkView;
  group: LaborCalendarGuestLinkGroup;
  colorBy: LaborCalendarGuestLinkColorMode;
  anchorDate: string | null;
  windowStart: string;
  windowEnd: string;
  locationIds: string[];
  eventIds: string[];
  roleIds: string[];
  people: string[];
  publishStatus: LaborCalendarPublishStatus;
  assignmentStatus: LaborCalendarAssignmentStatus;
  eventOverlay: boolean;
  revenueDateBasis: LaborCalendarRevenueDateBasis;
  resourceGroupFilter: LaborCalendarGuestLinkResourceGroupFilter;
  resourceSort: LaborCalendarGuestLinkResourceSort;
};
export type LaborCalendarGuestLink = {
  id: string;
  label: string;
  filters: LaborCalendarGuestLinkFilters;
  activeFrom: string;
  activeUntil: string | null;
  createdAt: string | null;
};
export type LaborCalendarGuestLinkStatus = 'active' | 'upcoming' | 'expired' | 'revoked';
export type LaborCalendarGuestLinkAdminRow = LaborCalendarGuestLink & {
  status: LaborCalendarGuestLinkStatus;
  revokedAt: string | null;
  lastAccessedAt: string | null;
  createdBy: {
    id: string;
    name: string;
    email: string | null;
  } | null;
};
export type CreateLaborCalendarGuestLinkParams = {
  label: string;
  activeFrom: string;
  activeUntil?: string | null;
  filters: LaborCalendarGuestLinkFilters;
};
export type UpdateLaborCalendarGuestLinkParams = Partial<CreateLaborCalendarGuestLinkParams>;
export type CreateLaborCalendarGuestLinkResult = {
  token: string;
  path: string;
  link: LaborCalendarGuestLink;
};
export type LaborCalendarGuestSchedulePayload = {
  generatedAt: string;
  link: LaborCalendarGuestLink;
  shifts: LaborScheduleShift[];
};

export type LaborCalendarL2RFilters = Pick<
  LaborCalendarFilters,
  'windowStart' | 'windowEnd' | 'scope' | 'locationIds' | 'eventId' | 'eventIds' | 'personProfileIds' | 'freelancerIds' | 'includeUnconfirmedEvents' | 'revenueDateBasis'
>;

export type LaborCalendarRevenueEvent = {
  eventId: string;
  eventCode: string | null;
  name: string;
  status: string | null;
  siteLocationId: string | null;
  locationName: string | null;
  startDate: string | null;
  endDate: string | null;
  basisDate: string | null;
  netRevenue: number;
};

export type LaborCalendarL2RSummary = {
  estimatedWages: number | null;
  overtimeCost: number | null;
  canViewDetails?: boolean;
  canViewWageTotals?: boolean;
  /** Why breakdowns are closed for this view, from the server. */
  restriction?: string | null;
  scheduledMinutes: number;
  overtimeMinutes: number;
  laborPercent: number | null;
  absenceCount: number;
  shiftCount: number;
  netRevenue: number;
  goalLaborPercent: number | null;
  revenueEvents: LaborCalendarRevenueEvent[];
};

export type LaborCalendarRole = {
  id: string;
  name: string;
  code: string | null;
  active: boolean;
  /** Offered for Installation work; the others are event show roles. */
  installationEnabled: boolean;
};

export type LaborCalendarEventOption = {
  id: string;
  name: string;
  eventCode: string | null;
  siteLocationId: string | null;
  eventVenueId: string | null;
  venueId: string | null;
  venueName: string | null;
  venueAddress: string | null;
  laborCalendarColor: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  projectManagerProfileId: string | null;
  salesPersonProfileId: string | null;
  salesManagerAuthId: string | null;
};

export type LaborCalendarEventQueryRange = {
  windowStart?: string | null;
  windowEnd?: string | null;
};

export type LaborCalendarRoomOption = {
  id: string;
  eventId: string;
  name: string;
};

export type LaborCalendarTimeOffRequest = {
  id: string;
  profileId: string | null;
  freelancerId: string | null;
  startsAt: string;
  endsAt: string;
  status: 'requested' | 'approved' | 'rejected' | 'cancelled';
  reason: string | null;
  /** The location the request is for; null for requests saved without one. */
  siteLocationId: string | null;
  updatedAt?: string | null;
  allDay?: boolean | null;
  timeZone?: string | null;
};

export type LaborCandidateSuggestion = {
  resource: LaborScheduleResource;
  score: number;
  reasons: string[];
  blocked: boolean;
  roleAssignmentCount: number;
};

export const laborCalendarKeys = {
  all: ['labor-calendar'] as const,
  window: (filters: LaborCalendarFilters): QueryKey => [
    ...laborCalendarKeys.all,
    'window',
    filters.windowStart,
    filters.windowEnd,
    filters.scope,
    (filters.locationIds ?? []).join(','),
    filters.eventId ?? '',
    (filters.eventIds ?? []).join(','),
    filters.roleId ?? '',
    (filters.roleIds ?? []).join(','),
    filters.personProfileId ?? '',
    (filters.personProfileIds ?? []).join(','),
    filters.freelancerId ?? '',
    (filters.freelancerIds ?? []).join(','),
    filters.publishStatus ?? 'all',
    filters.assignmentStatus ?? 'all',
    filters.myEventsOnly ? 'my-events' : '',
    filters.includeUnconfirmedEvents ? 'include-unconfirmed' : '',
    filters.revenueDateBasis ?? 'start',
  ],
  l2r: (filters: LaborCalendarFilters): QueryKey => {
    const summaryFilters = normalizeLaborCalendarL2RFilters(filters);
    return [
      ...laborCalendarKeys.all,
      'l2r',
      summaryFilters.windowStart,
      summaryFilters.windowEnd,
      summaryFilters.scope,
      (summaryFilters.locationIds ?? []).join(','),
      summaryFilters.eventId ?? '',
      (summaryFilters.eventIds ?? []).join(','),
      (summaryFilters.personProfileIds ?? []).join(','),
      (summaryFilters.freelancerIds ?? []).join(','),
      summaryFilters.includeUnconfirmedEvents ? 'include-unconfirmed' : '',
      summaryFilters.revenueDateBasis ?? 'start',
    ];
  },
  resources: (locationIds?: string[] | null): QueryKey => [
    'labor-calendar',
    'resources',
    (locationIds ?? []).join(','),
  ],
  roles: ['labor-calendar', 'roles'] as const,
  events: (locationIds?: string[] | null, range?: LaborCalendarEventQueryRange | null): QueryKey => [
    'labor-calendar',
    'events',
    (locationIds ?? []).join(','),
    range?.windowStart ?? '',
    range?.windowEnd ?? '',
  ],
  rooms: (eventIds?: string[] | null): QueryKey => ['labor-calendar', 'rooms', (eventIds ?? []).join(',')],
  timeOff: (windowStart: string, windowEnd: string): QueryKey => ['labor-calendar', 'time-off', windowStart, windowEnd],
  activeClocks: (windowStart: string, windowEnd: string): QueryKey => ['labor-calendar', 'active-clocks', windowStart, windowEnd],
  eventVehicleTrips: (eventId?: string | null): QueryKey => ['labor-calendar', 'event-vehicle-trips', eventId ?? ''],
  history: (shiftId: string): QueryKey => [
    ...laborCalendarKeys.all,
    'history',
    shiftId,
  ],
  viewHistory: (query: LaborScheduleViewHistoryQuery): QueryKey => [
    ...laborCalendarKeys.all,
    'view-history',
    query.windowStart,
    query.windowEnd,
    query.shiftIds.slice().sort().join(','),
    query.locationIds.slice().sort().join(','),
    query.eventIds.slice().sort().join(','),
    query.roleIds.slice().sort().join(','),
    query.serviceTicketId ?? '',
    query.scope,
    query.search.trim().toLowerCase(),
    query.changeKinds.slice().sort().join(','),
    query.actorKeys.slice().sort().join(','),
    query.filterLocationIds.slice().sort().join(','),
    query.changedFrom ?? '',
    query.changedTo ?? '',
  ],
  viewHistoryPage: (query: LaborScheduleViewHistoryQuery): QueryKey => [
    ...laborCalendarKeys.viewHistory(query),
    'page',
    query.sort,
    query.sortDesc ? 'desc' : 'asc',
  ],
  viewHistoryFacets: (query: LaborScheduleViewHistoryQuery): QueryKey => [
    ...laborCalendarKeys.viewHistory(query),
    'facets',
  ],
  eventHoverDetails: (eventIds?: string[] | null): QueryKey => [
    'labor-calendar',
    'event-hover-details',
    (eventIds ?? []).slice().sort().join(','),
  ],
};

export function normalizeLaborCalendarL2RFilters(filters: LaborCalendarFilters): LaborCalendarL2RFilters {
  const locationIds = Array.from(new Set(filters.locationIds?.filter(Boolean) ?? [])).sort();
  return {
    windowStart: filters.windowStart,
    windowEnd: filters.windowEnd,
    scope: 'team',
    locationIds: locationIds.length > 0 ? locationIds : null,
    eventId: filters.eventId || null,
    eventIds: Array.from(new Set(filters.eventIds?.filter(Boolean) ?? [])).sort(),
    personProfileIds: filters.personProfileIds ?? (filters.personProfileId ? [filters.personProfileId] : []),
    freelancerIds: filters.freelancerIds ?? (filters.freelancerId ? [filters.freelancerId] : []),
    includeUnconfirmedEvents: filters.includeUnconfirmedEvents === true,
    revenueDateBasis: filters.revenueDateBasis === 'end' ? 'end' : 'start',
  };
}

function asString(value: unknown): string {
  return value == null ? '' : String(value);
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value);
  return text ? text : null;
}

function asNullableRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function asPostgrestLikeError(value: unknown): Partial<PostgrestError> | null {
  return value && typeof value === 'object' ? value as Partial<PostgrestError> : null;
}

export function laborScheduleMutationError(value: unknown): Error {
  if (value instanceof LaborScheduleConflictError) return value;
  const colorTaken = scheduleColorTakenError(value);
  if (colorTaken) return colorTaken;
  const error = asPostgrestLikeError(value);
  const isPastShiftLocked = error?.message === 'labor_schedule_past_shift_locked'
    || error?.details?.includes('Past shifts are read-only.')
    || error?.details?.includes('current or previous calendar week');
  if (isPastShiftLocked) return new Error('Only shifts in the current or previous calendar week can be edited.');
  if ((error?.code === 'PT409' || error?.code === '40001')
    && error.message === 'The original assignment changed. Reload the schedule before moving it.') {
    return new LaborScheduleConflictError([], true);
  }
  const isConflict = error?.code === '40001' || error?.message === 'labor_schedule_conflict';
  if (isConflict) {
    let parsed: { conflicts?: unknown } | null = null;
    try {
      parsed = error?.details ? JSON.parse(error.details) as { conflicts?: unknown } : null;
    } catch {
      parsed = null;
    }
    const conflicts = Array.isArray(parsed?.conflicts)
      ? parsed.conflicts.flatMap((entry): LaborScheduleConflict[] => {
        if (!entry || typeof entry !== 'object') return [];
        const row = entry as Record<string, unknown>;
        const shiftId = asString(row.shift_id);
        if (!shiftId) return [];
        return [{
          shiftId,
          expectedUpdatedAt: asNullableString(row.expected_updated_at),
          actualUpdatedAt: asNullableString(row.actual_updated_at),
        }];
      })
      : [];
    return new LaborScheduleConflictError(conflicts);
  }
  if (value instanceof Error) return value;
  return new Error(error?.message || 'Schedule save failed.');
}

function isoTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function intervalTouchesWindow(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  windowStart: number,
  windowEnd: number,
): boolean {
  const start = isoTime(startsAt);
  if (start == null) return false;
  const end = isoTime(endsAt);
  return start < windowEnd && (end == null ? start >= windowStart : end > windowStart);
}

export function laborScheduleBroadcastTouchesWindow(
  payload: LaborScheduleBroadcastPayload,
  filters: LaborScheduleBroadcastWindow,
): boolean {
  const locationIds = new Set(filters.locationIds ?? []);
  const payloadLocations = [payload.site_location_id, payload.old_site_location_id].filter(Boolean) as string[];
  if (locationIds.size > 0 && payloadLocations.length > 0 && !payloadLocations.some(id => locationIds.has(id))) {
    return false;
  }

  const eventIds = new Set(filters.eventIds ?? []);
  const payloadEvents = [payload.event_id, payload.old_event_id].filter(Boolean) as string[];
  if (eventIds.size > 0 && payloadEvents.length > 0 && !payloadEvents.some(id => eventIds.has(id))) {
    return false;
  }

  const windowStart = isoTime(filters.windowStart);
  const windowEnd = isoTime(filters.windowEnd);
  if (windowStart == null || windowEnd == null) return true;
  const hasTime = Boolean(payload.starts_at || payload.old_starts_at);
  if (!hasTime) return true;
  return intervalTouchesWindow(payload.starts_at, payload.ends_at, windowStart, windowEnd)
    || intervalTouchesWindow(payload.old_starts_at, payload.old_ends_at, windowStart, windowEnd);
}

const UUID_TEXT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuidText(value: string | null | undefined): value is string {
  return Boolean(value && UUID_TEXT_PATTERN.test(value.trim()));
}

function normaliseEventQueryDate(value: unknown): string | null {
  if (!value) return null;
  const date = new Date(String(value));
  if (Number.isFinite(date.getTime())) return date.toISOString().slice(0, 10);
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

// The recent-event list below is deliberately not tied to the calendar window, so a
// manager can still pick an event outside the days on screen. With no lower bound it
// read every event ever recorded at the location (7k+ at the busier sites), and the
// events RLS predicate is evaluated per row, so the query ran for tens of seconds and
// tripped the 8s statement timeout. Bounding the lookback keeps the picker useful while
// capping how many rows that predicate has to visit.
const RECENT_EVENT_LOOKBACK_MONTHS = 6;

// Anchored to the first of the month so the bound is stable across a day and cannot
// overflow (e.g. Aug 31 minus 6 months). Must stay a plain YYYY-MM-DD literal: Postgres
// only evaluates the date filter ahead of the RLS predicate when it compares against a
// constant, and loses that ordering if the bound is computed in SQL.
function recentEventLookbackStartYmd(now: Date = new Date()): string {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - RECENT_EVENT_LOOKBACK_MONTHS, 1));
  return start.toISOString().slice(0, 10);
}

function asNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeEquipmentSummary(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item => asString(item).trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/\r?\n|,/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
}

function emptyEventHoverDetails(): LaborScheduleEventHoverDetails {
  return {
    salesPersonName: null,
    customerCompanyName: null,
    clientName: null,
    grandTotal: null,
    paymentTerms: null,
    equipmentSummary: [],
    venueAddress: null,
  };
}

type EventHoverSourceRow = Record<string, unknown>;

function normalizeEventHoverDetails(row: EventHoverSourceRow): LaborScheduleEventHoverDetails {
  return {
    salesPersonName: asNullableString(row.sales_person_name ?? row.salesPersonName),
    customerCompanyName: asNullableString(row.customer_company_name ?? row.client_business ?? row.clientBusiness),
    clientName: asNullableString(row.client_name ?? row.clientName),
    grandTotal: asNullableNumber(row.grand_total ?? row.grandTotal),
    paymentTerms: asNullableString(row.payment_terms ?? row.paymentTerms),
    equipmentSummary: normalizeEquipmentSummary(row.equipment_summary ?? row.equipmentSummary),
    venueAddress: asNullableString(row.venue_address ?? row.venueAddress)
      ?? normalizeVenueAddress(row as EventVenueSourceRow),
  };
}

function asScheduleType(value: unknown): LaborScheduleType {
  const text = asString(value);
  return ['labor', 'pick', 'return', 'delivery', 'pickup', 'service', 'other'].includes(text)
    ? text as LaborScheduleType
    : 'labor';
}

function asScheduleSource(value: unknown): LaborScheduleSource {
  const text = asString(value);
  return ['manual', 'event_labor', 'event_logistics', 'service_ticket', 'installation', 'time_tracking'].includes(text)
    ? text as LaborScheduleSource
    : 'manual';
}

export function isLogisticsScheduleType(value: LaborScheduleType | string | null | undefined): boolean {
  return value === 'pick' || value === 'return' || value === 'delivery' || value === 'pickup';
}

function isLogisticsShiftLike(shift: Pick<LaborScheduleShift, 'source' | 'scheduleType'>): boolean {
  return shift.source === 'event_logistics' || isLogisticsScheduleType(shift.scheduleType);
}

function logisticsRequirementEventIds(shift: Pick<LaborScheduleShift, 'eventId' | 'events'>): string[] {
  const ids = new Set<string>();
  if (shift.eventId) ids.add(shift.eventId);
  for (const event of shift.events) {
    if (event.id) ids.add(event.id);
  }
  return Array.from(ids);
}

export function filterLogisticsShiftsByEventRequirement(
  shifts: LaborScheduleShift[],
  requiresLogisticsByEventId: Map<string, boolean>,
): LaborScheduleShift[] {
  if (!requiresLogisticsByEventId.size) return shifts;
  return shifts.filter(shift => {
    if (!isLogisticsShiftLike(shift)) return true;
    const eventIds = logisticsRequirementEventIds(shift);
    if (!eventIds.length) return true;
    const knownEventIds = eventIds.filter(eventId => requiresLogisticsByEventId.has(eventId));
    if (!knownEventIds.length) return true;
    return knownEventIds.some(eventId => requiresLogisticsByEventId.get(eventId) !== false);
  });
}

export function filterGeneratedLogisticsShiftsForScope(
  shifts: LaborScheduleShift[],
  scope: LaborCalendarScope,
): LaborScheduleShift[] {
  if (scope === 'logistics' || scope === 'mine') return shifts;
  return shifts.filter(shift => shift.source !== 'event_logistics');
}

async function filterUnneededLogisticsShifts(
  shifts: LaborScheduleShift[],
  supabase: SupabaseClient,
): Promise<LaborScheduleShift[]> {
  const eventIds = Array.from(new Set(shifts
    .filter(isLogisticsShiftLike)
    .flatMap(logisticsRequirementEventIds)));
  if (!eventIds.length) return shifts;

  try {
    const requiresLogisticsByEventId = new Map<string, boolean>();
    const { data, error } = await markRequest(supabase.rpc('labor_schedule_event_logistics_requirements', { p_event_ids: eventIds }), 'read');
    if (error) throw error;
    for (const eventId of eventIds) requiresLogisticsByEventId.set(eventId, data?.[eventId] === true);
    const sameSiteEventIds = await fetchSameSiteLogisticsEventIds(eventIds, supabase);
    for (const eventId of sameSiteEventIds) {
      requiresLogisticsByEventId.set(eventId, false);
    }
    return filterLogisticsShiftsByEventRequirement(shifts, requiresLogisticsByEventId);
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[labor-calendar] logistics requirement filter failed', err);
    }
    throw err;
  }
}

function localDateTimeParts(value: string | null | undefined): { date: string; time: string } | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const pad = (part: number) => String(part).padStart(2, '0');
  return {
    date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
  };
}

function primaryEventIdForShift(shift: LogisticsWarehouseShift | null | undefined): string | null {
  if (!shift) return null;
  if (shift.eventId) return shift.eventId;
  const primary = shift.events.find(event => event.id);
  return primary?.id ?? null;
}

export function eventWarehousePatchForLogisticsShift(
  shift: Pick<LaborScheduleShift, 'scheduleType' | 'source'> | null | undefined,
  startsAt: string | null | undefined,
): LaborScheduleEventWarehousePatch | null {
  if (!shift || (shift.source !== 'event_logistics' && !isLogisticsScheduleType(shift.scheduleType))) return null;
  const parts = localDateTimeParts(startsAt);
  if (!parts) return null;
  switch (shift.scheduleType) {
    case 'pick':
      return { fill_date: parts.date, fill_time: parts.time };
    case 'delivery':
      return { ship_date: parts.date, ship_time: parts.time };
    case 'pickup':
      return { load_out_date: parts.date, load_out_time: parts.time };
    case 'return':
      return { return_date: parts.date, return_time: parts.time };
    default:
      return null;
  }
}

async function syncEventWarehouseFieldsForLogisticsShift(
  shift: LogisticsWarehouseShift | null | undefined,
  startsAt: string | null | undefined,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const patch = eventWarehousePatchForLogisticsShift(shift, startsAt);
  const eventId = primaryEventIdForShift(shift);
  if (!patch || !eventId || !shift?.id) return null;
  const supabase = options?.supabase ?? getSupabaseClient();
  const rpcResult = await markRequest(supabase.rpc('labor_schedule_sync_event_logistics_dates', {
    p_shift_id: shift.id,
    p_starts_at: startsAt || shift.startsAt,
  }), 'mutation');
  if (!rpcResult.error) return null;
  if (!isMissingOptionalLaborTable(rpcResult.error, 'labor_schedule_sync_event_logistics_dates')) {
    return rpcResult.error;
  }
  const { error } = await supabase
    .from('events')
    .update(patch)
    .eq('id', eventId);
  return error ?? null;
}

function normalizeAssignments(value: unknown): LaborScheduleAssignment[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row: any) => ({
      assignmentId: asString(row.assignment_id),
      profileId: asNullableString(row.profile_id),
      freelancerId: asNullableString(row.freelancer_id),
      assignmentType: row.assignment_type === 'freelancer' ? 'freelancer' as const : 'employee' as const,
      status: (row.status || 'assigned') as LaborScheduleAssignment['status'],
      costRate: row.cost_rate == null ? null : asNumber(row.cost_rate),
      displayName: asString(row.display_name) || 'Unnamed team member',
      email: asNullableString(row.email),
    }))
    .filter(row => row.assignmentId);
}

function normalizeVehicleAssignments(value: unknown): LaborScheduleVehicleAssignment[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row: any) => ({
      assignmentId: asString(row.assignment_id),
      shiftId: asString(row.shift_id),
      vehicleId: asString(row.vehicle_id),
      status: (row.status || 'assigned') as LaborScheduleVehicleAssignment['status'],
      loadStartsAt: asNullableString(row.load_starts_at),
      returnAt: asNullableString(row.return_at),
      driverProfileId: asNullableString(row.driver_profile_id),
      driverFreelancerId: asNullableString(row.driver_freelancer_id),
      driverName: asNullableString(row.driver_name),
      driverEmail: asNullableString(row.driver_email),
      transitMinutes: asNullableNumber(row.transit_minutes),
      eventDriveMinutes: asNullableNumber(row.event_drive_minutes),
      effectiveTransitMinutes: asNullableNumber(row.effective_transit_minutes),
      displayName: asString(row.display_name || row.vehicle_name || row.name) || 'Unnamed vehicle',
      vehicleType: asNullableString(row.vehicle_type),
      licensePlate: asNullableString(row.license_plate),
      capacity: asNullableString(row.capacity),
      locationId: asNullableString(row.location_id),
      locationName: asNullableString(row.location_name),
      notes: asNullableString(row.notes),
    }))
    .filter(row => row.assignmentId && row.vehicleId);
}

function normalizeEventVehicleTrips(value: unknown): LaborScheduleEventVehicleTrip[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row: any) => {
      const [assignment] = normalizeVehicleAssignments([row]);
      if (!assignment) return null;
      return {
        ...assignment,
        eventId: asNullableString(row.event_id),
        eventName: asNullableString(row.event_name),
        eventCode: asNullableString(row.event_code),
        scheduleType: asScheduleType(row.schedule_type),
        roleName: asNullableString(row.role_name),
        startsAt: asNullableString(row.starts_at),
        endsAt: asNullableString(row.ends_at),
      };
    })
    .filter((row): row is LaborScheduleEventVehicleTrip => Boolean(row));
}

type EventDateSourceRow = {
  event_start_date?: unknown;
  eventStartDate?: unknown;
  show_start_date?: unknown;
  showStartDate?: unknown;
  start_date?: unknown;
  startDate?: unknown;
  event_end_date?: unknown;
  eventEndDate?: unknown;
  show_end_date?: unknown;
  showEndDate?: unknown;
  end_date?: unknown;
  endDate?: unknown;
};

type EventVenueSourceRow = {
  id?: unknown;
  event_id?: unknown;
  eventVenueId?: unknown;
  event_venue_id?: unknown;
  venue_id?: unknown;
  venueId?: unknown;
  venue_name?: unknown;
  venueName?: unknown;
  address_line1?: unknown;
  address_line2?: unknown;
  city?: unknown;
  state?: unknown;
  postal_code?: unknown;
  country?: unknown;
  position?: unknown;
  created_at?: unknown;
  venues?: {
    venue_name?: unknown;
    address_line1?: unknown;
    address_line2?: unknown;
    city?: unknown;
    state?: unknown;
    postal_code?: unknown;
    country?: unknown;
  } | Array<{
    venue_name?: unknown;
    address_line1?: unknown;
    address_line2?: unknown;
    city?: unknown;
    state?: unknown;
    postal_code?: unknown;
    country?: unknown;
  }> | null;
};

type LaborCalendarEventVenueSummary = {
  sites?: LaborScheduleJobSite[];
  eventVenueId: string | null;
  venueId: string | null;
  venueName: string | null;
  venueAddress: string | null;
};

function normalizeEventStartDate(row: EventDateSourceRow): string | null {
  return asNullableString(
    row.event_start_date
    ?? row.eventStartDate
    ?? row.show_start_date
    ?? row.showStartDate
    ?? row.start_date
    ?? row.startDate,
  );
}

function normalizeEventEndDate(row: EventDateSourceRow): string | null {
  const startDate = normalizeEventStartDate(row);
  return asNullableString(
    row.event_end_date
    ?? row.eventEndDate
    ?? row.show_end_date
    ?? row.showEndDate
    ?? row.end_date
    ?? row.endDate
    ?? startDate,
  );
}

function normalizeVenueRelationName(value: EventVenueSourceRow['venues']): string | null {
  const relation = Array.isArray(value) ? value[0] : value;
  return asNullableString(relation?.venue_name);
}

function normalizeVenueAddressParts(row: EventVenueSourceRow): JobAddress {
  const relation = Array.isArray(row.venues) ? row.venues[0] : row.venues;
  const source = [row.address_line1, row.address_line2, row.city, row.state, row.postal_code, row.country].some(value => asNullableString(value)) ? row : relation;
  return { line1: asNullableString(source?.address_line1), line2: asNullableString(source?.address_line2),
    city: asNullableString(source?.city), state: asNullableString(source?.state),
    postalCode: asNullableString(source?.postal_code), country: asNullableString(source?.country) };
}

function normalizeVenueAddress(row: EventVenueSourceRow): string | null {
  return jobAddressLines(normalizeVenueAddressParts(row)).join(', ') || null;
}

function normalizeEventVenueSummary(
  row: EventVenueSourceRow,
  options?: { rowIdIsEventVenueId?: boolean },
): LaborCalendarEventVenueSummary {
  return {
    eventVenueId: asNullableString(
      row.event_venue_id
      ?? row.eventVenueId
      ?? (options?.rowIdIsEventVenueId ? row.id : null),
    ),
    venueId: asNullableString(row.venue_id ?? row.venueId),
    venueName: asNullableString(row.venue_name ?? row.venueName) ?? normalizeVenueRelationName(row.venues),
    venueAddress: normalizeVenueAddress(row),
  };
}

function compareEventVenueRows(a: EventVenueSourceRow, b: EventVenueSourceRow): number {
  const leftPosition = asNullableNumber(a.position) ?? Number.MAX_SAFE_INTEGER;
  const rightPosition = asNullableNumber(b.position) ?? Number.MAX_SAFE_INTEGER;
  if (leftPosition !== rightPosition) return leftPosition - rightPosition;
  const createdCompare = (asNullableString(a.created_at) ?? '').localeCompare(asNullableString(b.created_at) ?? '');
  if (createdCompare !== 0) return createdCompare;
  return asString(a.id).localeCompare(asString(b.id));
}

async function fetchPrimaryEventVenues(
  eventIds: string[],
  supabase: SupabaseClient,
): Promise<Map<string, LaborCalendarEventVenueSummary>> {
  const ids = Array.from(new Set(eventIds.map(id => id.trim()).filter(Boolean)));
  const venuesByEventId = new Map<string, LaborCalendarEventVenueSummary>();
  if (!ids.length) return venuesByEventId;

  const { data, error } = await supabase
    .from('event_venues')
    .select('id,event_id,venue_id,venue_name,address_line1,address_line2,city,state,postal_code,country,position,created_at,venues(venue_name,address_line1,address_line2,city,state,postal_code,country)')
    .in('event_id', ids)
    .is('deleted_at', null);
  if (error || !Array.isArray(data)) return venuesByEventId;

  const grouped = new Map<string, EventVenueSourceRow[]>();
  for (const row of data as EventVenueSourceRow[]) {
    const eventId = asString(row.event_id);
    if (!eventId) continue;
    const current = grouped.get(eventId) ?? [];
    current.push(row);
    grouped.set(eventId, current);
  }

  for (const [eventId, rows] of grouped.entries()) {
    const [primary] = rows.sort(compareEventVenueRows);
    if (primary) venuesByEventId.set(eventId, {
      ...normalizeEventVenueSummary(primary, { rowIdIsEventVenueId: true }),
      sites: rows.map(row => ({ id: asString(row.id), venueId: asNullableString(row.venue_id), locationName: asNullableString(row.venue_name) || normalizeVenueRelationName(row.venues), label: asNullableString(row.venue_name) || normalizeVenueRelationName(row.venues) || 'Job site', address: normalizeVenueAddress(row), addressParts: normalizeVenueAddressParts(row) })),
    });
  }

  return venuesByEventId;
}

function normalizeEventLinks(value: unknown, row: any): LaborScheduleEventLink[] {
  const rows = Array.isArray(value) ? value : [];
  const links = rows
    .map((event: any) => {
      const details = normalizeEventHoverDetails(event);
      return {
        id: asString(event.id),
        name: asString(event.name || event.event_name || event.event_code) || 'Untitled event',
        eventCode: asNullableString(event.event_code),
        siteLocationId: asNullableString(event.site_location_id),
        ...normalizeEventVenueSummary(event),
        laborCalendarColor: asNullableString(event.labor_calendar_color || event.laborCalendarColor),
        startDate: normalizeEventStartDate(event),
        endDate: normalizeEventEndDate(event),
        ...details,
      };
    })
    .filter(event => event.id);
  if (links.length) return links;
  const eventId = asNullableString(row.event_id);
  if (!eventId) return [];
  const details = normalizeEventHoverDetails(row);
  return [{
    id: eventId,
    name: asString(row.event_name || row.event_code) || 'Untitled event',
    eventCode: asNullableString(row.event_code),
    siteLocationId: asNullableString(row.site_location_id),
    ...normalizeEventVenueSummary(row),
    laborCalendarColor: asNullableString(row.labor_calendar_color),
    startDate: normalizeEventStartDate(row),
    endDate: normalizeEventEndDate(row),
    ...details,
  }];
}

export function mapLaborScheduleShift(row: any): LaborScheduleShift {
  const events = normalizeEventLinks(row.events, row);
  const rawRecurrenceRule = asNullableRecord(row.recurrence_rule);
  const recurrenceRule = rawRecurrenceRule
    && ['day', 'week', 'month', 'year'].includes(asString(rawRecurrenceRule.frequency))
    ? {
      frequency: asString(rawRecurrenceRule.frequency) as LaborScheduleRecurrenceRule['frequency'],
      interval: Math.max(1, asNumber(rawRecurrenceRule.interval, 1)),
      weekdays: Array.isArray(rawRecurrenceRule.weekdays)
        ? rawRecurrenceRule.weekdays.map(value => asNumber(value, -1)).filter(value => value >= 0 && value <= 6)
        : [],
      untilDate: asString(rawRecurrenceRule.untilDate),
      anchorDate: asString(rawRecurrenceRule.anchorDate),
      timeZone: asString(rawRecurrenceRule.timeZone) || 'UTC',
    }
    : null;
  return {
    id: asString(row.shift_id),
    eventId: asNullableString(row.event_id),
    eventName: asString(row.event_name) || (events.length > 1 ? `${events.length} linked events` : events[0]?.name) || 'Independent shift',
    eventCode: asNullableString(row.event_code),
    events,
    eventRoomId: asNullableString(row.event_room_id),
    roomName: asNullableString(row.room_name),
    laborRoleId: asNullableString(row.labor_role_id),
    roleName: asNullableString(row.role_name),
    siteLocationId: asNullableString(row.site_location_id),
    locationName: asNullableString(row.location_name),
    startsAt: asString(row.starts_at),
    endsAt: asNullableString(row.ends_at),
    unpaidBreakMinutes: row.unpaid_break_minutes == null ? null : asNumber(row.unpaid_break_minutes, 0),
    unpaidBreaks: normalizeUnpaidBreaks(row.unpaid_breaks),
    lunchBreakMinutes: row.lunch_break_minutes == null ? null : asNumber(row.lunch_break_minutes, 0),
    lunchBreakClassification: row.lunch_break_classification == null ? 'lunch' : breakClassification(row.lunch_break_classification),
    dailyBreaks: Array.isArray(row.daily_breaks) ? row.daily_breaks : [],
    slingUnpaidBreakMinutes: row.sling_unpaid_break_minutes == null ? null : asNumber(row.sling_unpaid_break_minutes, 0),
    updatedAt: asNullableString(row.updated_at),
    requiredPeople: Math.max(1, asNumber(row.required_people, 1)),
    scheduleStatus: (row.schedule_status || 'planned') as LaborScheduleShift['scheduleStatus'],
    publishStatus: (row.publish_status || 'draft') as LaborScheduleShift['publishStatus'],
    scheduleType: asScheduleType(row.schedule_type),
    source: asScheduleSource(row.source),
    isOpen: row.is_open === true,
    isTentative: row.is_tentative === true,
    needsTime: row.needs_time === true,
    shiftLabel: asNullableString(row.shift_label),
    recurrenceSeriesId: asNullableString(row.recurrence_series_id),
    recurrenceRule,
    recurrenceIndex: row.recurrence_index == null ? null : Math.max(1, asNumber(row.recurrence_index, 1)),
    notes: asNullableString(row.notes),
    assignmentCount: asNumber(row.assignment_count),
    shortage: asNumber(row.shortage),
    conflictCount: asNumber(row.conflict_count),
    assignments: normalizeAssignments(row.assignments),
    vehicleAssignments: normalizeVehicleAssignments(row.vehicle_assignments),
    serviceTicketId: asNullableString(row.service_ticket_id ?? row.serviceTicketId),
    serviceTicketLaborLineId: asNullableString(row.service_ticket_labor_line_id ?? row.serviceTicketLaborLineId),
    installationJobId: asNullableString(row.installation_job_id ?? row.installationJobId),
    installationRoomId: asNullableString(row.installation_room_id ?? row.installationRoomId),
    installationLaborLineId: asNullableString(row.installation_labor_line_id ?? row.installationLaborLineId),
    timeEntryId: asNullableString(row.time_entry_id ?? row.timeEntryId),
    plannedStartsAt: asNullableString(row.planned_starts_at ?? row.plannedStartsAt),
    plannedEndsAt: asNullableString(row.planned_ends_at ?? row.plannedEndsAt),
    calendarColor: asNullableString(row.calendar_color),
    installationCalendarColor: asNullableString(row.installation_calendar_color),
    serviceTicketCalendarColor: asNullableString(row.service_ticket_calendar_color),
  };
}

/** Recorded work: a recorded-only shift, or a planned shift that now holds a time entry. */
export function isRecordedShift(shift: Pick<LaborScheduleShift, 'source' | 'timeEntryId'> | null | undefined): boolean {
  return Boolean(shift && (shift.source === 'time_tracking' || shift.timeEntryId));
}

export function mapLaborScheduleHistoryEntry(row: Record<string, unknown>): LaborScheduleHistoryEntry {
  return {
    historyId: asString(row.history_id),
    shiftId: asString(row.shift_id),
    assignmentId: asNullableString(row.assignment_id),
    entityType: asString(row.entity_type) as LaborScheduleHistoryEntry['entityType'],
    operation: asString(row.operation) as LaborScheduleHistoryEntry['operation'],
    activityType: asString(row.activity_type),
    actorProfileId: asNullableString(row.actor_profile_id),
    actorName: asString(row.actor_name) || 'System',
    actorEmail: asNullableString(row.actor_email),
    subjectLabel: asNullableString(row.subject_label),
    beforeData: asNullableRecord(row.before_data),
    afterData: asNullableRecord(row.after_data),
    changedFields: Array.isArray(row.changed_fields)
      ? row.changed_fields.map(asString).filter(Boolean)
      : [],
    transactionId: asNullableString(row.transaction_id),
    occurredAt: asString(row.occurred_at),
    isLegacy: row.is_legacy === true,
  };
}

export async function fetchLaborScheduleHistory(
  shiftId: string,
  options?: ClientOption,
): Promise<{ data: LaborScheduleHistoryEntry[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_history', {
    p_shift_id: shiftId,
    p_limit: 250,
  }), 'read');
  if (error) return { data: [], error };
  return {
    data: (Array.isArray(data) ? data : []).map(row => mapLaborScheduleHistoryEntry(row as Record<string, unknown>)),
    error: null,
  };
}

export const LABOR_SCHEDULE_VIEW_HISTORY_PAGE_SIZE = 100;
const uuidsOnly = (values: readonly string[]) => values.filter(isUuidText);

const nullIfEmpty = <T>(values: T[]): T[] | null => (values.length ? values : null);
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/** The view and filter arguments both Schedule › History functions take. */
function viewHistoryArgs(query: LaborScheduleViewHistoryQuery) {
  const search = query.search.trim();
  return {
    // Rows the Schedule draws without a saved shift (open actuals, service visits) have no history.
    p_shift_ids: uuidsOnly(query.shiftIds),
    p_window_start: query.windowStart,
    p_window_end: query.windowEnd,
    p_location_ids: nullIfEmpty(uuidsOnly(query.locationIds)),
    p_event_ids: nullIfEmpty(uuidsOnly(query.eventIds)),
    p_role_ids: nullIfEmpty(uuidsOnly(query.roleIds)),
    p_service_ticket_id: isUuidText(query.serviceTicketId) ? query.serviceTicketId : null,
    p_scope: query.scope,
    p_search: search || null,
    // A column menu with every value unchecked sends a value no row has, so it matches nothing.
    p_change_kinds: nullIfEmpty(query.changeKinds),
    p_actor_keys: nullIfEmpty(query.actorKeys),
    p_filter_location_ids: query.filterLocationIds.length
      ? nullIfEmpty(uuidsOnly(query.filterLocationIds)) ?? [NIL_UUID]
      : null,
    p_changed_from: query.changedFrom,
    p_changed_to: query.changedTo,
  };
}

/**
 * One page of the Schedule › History table, searched, filtered and sorted by the database, with the
 * total. Admins only: the function refuses anyone else.
 */
export async function fetchLaborScheduleViewHistoryPage(
  query: LaborScheduleViewHistoryQuery,
  offset: number,
  options?: ClientOption,
): Promise<{ data: LaborScheduleViewHistoryRow[]; total: number; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_view_history_page', {
    ...viewHistoryArgs(query),
    p_sort: query.sort,
    p_sort_desc: query.sortDesc,
    p_offset: offset,
    p_limit: LABOR_SCHEDULE_VIEW_HISTORY_PAGE_SIZE,
  }), 'read');
  if (error) return { data: [], total: 0, error };
  const rows = (Array.isArray(data) ? data : []) as Record<string, unknown>[];
  return {
    data: rows.map(row => ({
      ...mapLaborScheduleHistoryEntry(row),
      shiftDeleted: row.shift_deleted === true,
      shiftStartsAt: asNullableString(row.shift_starts_at),
      shiftEndsAt: asNullableString(row.shift_ends_at),
      shiftJobLabel: asNullableString(row.shift_job_label),
      shiftRoleName: asNullableString(row.shift_role_name),
      shiftLocationName: asNullableString(row.shift_location_name),
      shiftLocationId: asNullableString(row.shift_location_id),
      changeKind: (asString(row.change_kind) || 'edited') as LaborScheduleHistoryChangeKind,
      actorKey: asString(row.actor_key) || 'system',
    })),
    // Every row carries the total; a page past the end has none, and then nothing is left.
    total: rows.length ? Number(rows[0].total_count) || 0 : 0,
    error: null,
  };
}

/** The Change, Location and Made by columns' values with counts, for their filter menus. */
export async function fetchLaborScheduleViewHistoryFacets(
  query: LaborScheduleViewHistoryQuery,
  options?: ClientOption,
): Promise<{ data: LaborScheduleViewHistoryFacets; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const facets: LaborScheduleViewHistoryFacets = { kind: [], actor: [], location: [] };
  const { data, error } = await markRequest(
    supabase.rpc('labor_schedule_view_history_facets', viewHistoryArgs(query)),
    'read',
  );
  if (error) return { data: facets, error };
  for (const value of (Array.isArray(data) ? data : []) as Record<string, unknown>[]) {
    const facet = asString(value.facet);
    if (facet !== 'kind' && facet !== 'actor' && facet !== 'location') continue;
    facets[facet].push({
      // A shift with no location groups under an empty key.
      value: asString(value.facet_key),
      label: asString(value.facet_label),
      count: Number(value.change_count) || 0,
    });
  }
  return { data: facets, error: null };
}

async function fetchInstallationScheduleRows(
  filters: LaborCalendarFilters,
  supabase: SupabaseClient,
  authorizedWindowRows: LaborScheduleShift[],
): Promise<{ data: LaborScheduleShift[]; error: PostgrestError | null }> {
  const authorizedInstallations = new Map(authorizedWindowRows.filter(row => row.source === 'installation').map(row => [row.id, row]));
  if (filters.scope === 'logistics' || authorizedInstallations.size === 0) return { data: [], error: null };
  let currentProfileId: string | null = null;
  if (filters.scope === 'mine' || filters.scope === 'offers') {
    const authResult = await supabase.auth.getUser();
    const authId = authResult.data.user?.id ?? null;
    if (authId) {
      const profileResult = await supabase.from('user_profiles').select('id').eq('auth_user_id', authId).maybeSingle();
      if (!profileResult.error) currentProfileId = asNullableString((profileResult.data as any)?.id);
    }
  }
  let query = supabase
    .from('labor_schedule_shifts')
    .select(`shift_id,installation_job_id,installation_room_id,installation_labor_line_id,labor_role_id,site_location_id,
      starts_at,ends_at,updated_at,required_people,schedule_status,publish_status,schedule_type,source,is_open,is_tentative,needs_time,shift_label,notes,unpaid_break_minutes,sling_unpaid_break_minutes,unpaid_breaks,lunch_break_minutes,lunch_break_classification,
      time_entry_id,planned_starts_at,planned_ends_at,calendar_color,
      installation_jobs(job_code,name),installation_rooms(name),labor_roles(name),locations(name)`)
    .eq('source', 'installation')
    .lt('starts_at', filters.windowEnd);
  // Assignment membership comes only from the authorized native window RPC.
  // Direct assignment-table reads are deliberately unavailable to Auth users.
  query = (query as any).gt('ends_at', filters.windowStart).neq('schedule_status', 'cancelled');
  if (filters.locationIds?.length) query = query.in('site_location_id', filters.locationIds);
  if (filters.roleId) query = query.eq('labor_role_id', filters.roleId);
  if (filters.publishStatus && filters.publishStatus !== 'all') query = query.eq('publish_status', filters.publishStatus);
  const { data, error } = await query;
  if (error) return { data: [], error };
  const mapped: LaborScheduleShift[] = ((data ?? []) as any[]).map(row => {
    const assignments = authorizedInstallations.get(asString(row.shift_id))?.assignments ?? [];
    return {
      id: asString(row.shift_id), eventId: null, eventName: asString(row.installation_jobs?.name) || 'Installation',
      eventCode: asNullableString(row.installation_jobs?.job_code), events: [], eventRoomId: null,
      roomName: asNullableString(row.installation_rooms?.name), laborRoleId: asNullableString(row.labor_role_id),
      roleName: asNullableString(row.labor_roles?.name), siteLocationId: asNullableString(row.site_location_id),
      locationName: asNullableString(row.locations?.name), startsAt: asString(row.starts_at), endsAt: asNullableString(row.ends_at),
      updatedAt: asNullableString(row.updated_at),
      requiredPeople: Math.max(1, asNumber(row.required_people, 1)), scheduleStatus: (row.schedule_status || 'planned') as LaborScheduleShift['scheduleStatus'],
      publishStatus: row.publish_status === 'published' ? 'published' : 'draft', scheduleType: asScheduleType(row.schedule_type),
      source: 'installation', isOpen: row.is_open === true, isTentative: row.is_tentative === true, needsTime: row.needs_time === true,
      shiftLabel: asNullableString(row.shift_label), notes: asNullableString(row.notes),
      unpaidBreakMinutes: asNullableNumber(row.unpaid_break_minutes), slingUnpaidBreakMinutes: asNullableNumber(row.sling_unpaid_break_minutes),
      unpaidBreaks: normalizeUnpaidBreaks(row.unpaid_breaks), lunchBreakMinutes: asNullableNumber(row.lunch_break_minutes),
      lunchBreakClassification: row.lunch_break_classification == null ? 'lunch' : breakClassification(row.lunch_break_classification),
      assignmentCount: assignments.filter(item => !['cancelled','declined','withdrawn'].includes(item.status)).length,
      shortage: Math.max(0, asNumber(row.required_people, 1) - assignments.filter(item => !['cancelled','declined','withdrawn'].includes(item.status)).length),
      conflictCount: 0, assignments, vehicleAssignments: [], installationJobId: asNullableString(row.installation_job_id),
      installationLaborLineId: asNullableString(row.installation_labor_line_id),
      installationRoomId: asNullableString(row.installation_room_id),
      timeEntryId: asNullableString(row.time_entry_id),
      plannedStartsAt: asNullableString(row.planned_starts_at),
      plannedEndsAt: asNullableString(row.planned_ends_at),
      // These rows replace the window's installation rows, so they carry the visit's own color too.
      calendarColor: asNullableString(row.calendar_color),
    };
  });
  const filtered = mapped.filter(shift => {
    if (!authorizedInstallations.has(shift.id)) return false;
    if (filters.personProfileId && !shift.assignments.some(item => item.profileId === filters.personProfileId)) return false;
    if (filters.freelancerId && !shift.assignments.some(item => item.freelancerId === filters.freelancerId)) return false;
    if (filters.assignmentStatus && filters.assignmentStatus !== 'all' && !shift.assignments.some(item => item.status === filters.assignmentStatus)) return false;
    if (filters.scope === 'open' && !shift.isOpen) return false;
    if (filters.scope === 'mine' && (!currentProfileId || !shift.assignments.some(item => item.profileId === currentProfileId))) return false;
    if (filters.scope === 'offers' && (!currentProfileId || !shift.assignments.some(item => item.profileId === currentProfileId && ['offered','accepted'].includes(item.status)))) return false;
    if (filters.scope === 'requests' && !shift.assignments.some(item => item.status === 'requested')) return false;
    return true;
  });
  return { data: filtered, error: null };
}

/** The shared window RPC can already return Installation rows on databases
 * where the Installation migration is live. The direct read enriches those
 * rows with Installation job, room, and assignment labels. Prefer that exact
 * shift record instead of rendering both copies during a mixed-version rollout. */
export function mergeInstallationScheduleRows(
  baseRows: LaborScheduleShift[],
  directInstallationRows: LaborScheduleShift[],
): LaborScheduleShift[] {
  const directIds = new Set(directInstallationRows.map(row => row.id));
  return [
    ...baseRows.filter(row => !directIds.has(row.id)),
    ...directInstallationRows,
  ];
}

async function readLaborCalendarApiJson(response: Response, fallbackError: string): Promise<any> {
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok || body?.ok === false) {
    throw new Error(asString(body?.error) || fallbackError);
  }
  return body;
}

export async function createLaborCalendarGuestLink(
  params: CreateLaborCalendarGuestLinkParams,
): Promise<CreateLaborCalendarGuestLinkResult> {
  const response = await apiFetch('/labor-calendar/schedule-links', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  const body = await readLaborCalendarApiJson(response, 'schedule_link_create_failed');
  return {
    token: asString(body.token),
    path: asString(body.path),
    link: body.link as LaborCalendarGuestLink,
  };
}

export async function fetchLaborCalendarGuestLinks(): Promise<LaborCalendarGuestLinkAdminRow[]> {
  const response = await apiFetch('/labor-calendar/schedule-links');
  const body = await readLaborCalendarApiJson(response, 'schedule_link_list_failed');
  return Array.isArray(body.links) ? body.links as LaborCalendarGuestLinkAdminRow[] : [];
}

export async function updateLaborCalendarGuestLink(
  linkId: string,
  params: UpdateLaborCalendarGuestLinkParams,
): Promise<LaborCalendarGuestLinkAdminRow> {
  const response = await apiFetch(`/labor-calendar/schedule-links/${encodeURIComponent(linkId)}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  });
  const body = await readLaborCalendarApiJson(response, 'schedule_link_update_failed');
  return body.link as LaborCalendarGuestLinkAdminRow;
}

export async function revokeLaborCalendarGuestLink(linkId: string): Promise<LaborCalendarGuestLinkAdminRow> {
  const response = await apiFetch(`/labor-calendar/schedule-links/${encodeURIComponent(linkId)}/revoke`, {
    method: 'POST',
  });
  const body = await readLaborCalendarApiJson(response, 'schedule_link_revoke_failed');
  return body.link as LaborCalendarGuestLinkAdminRow;
}

export async function fetchLaborCalendarGuestSchedule(
  token: string,
): Promise<LaborCalendarGuestSchedulePayload> {
  const response = await apiFetch(
    `/labor-calendar/public/schedule-links/${encodeURIComponent(token)}`,
    {},
    { authMode: 'public' },
  );
  const body = await readLaborCalendarApiJson(response, 'schedule_link_load_failed');
  return {
    generatedAt: asString(body.generatedAt),
    link: body.link as LaborCalendarGuestLink,
    shifts: Array.isArray(body.shifts) ? body.shifts.filter((shift: { source?: string }) => shift.source !== 'sling') as LaborScheduleShift[] : [],
  };
}

type ServiceTicketScheduleRow = {
  service_ticket_id?: unknown;
  doc_no?: unknown;
  summary?: unknown;
  description?: unknown;
  status?: unknown;
  priority?: unknown;
  location_id?: unknown;
  requested_service_date?: unknown;
  scheduled_start_at?: unknown;
  updated_at?: unknown;
  service_location_name?: unknown;
  service_room?: unknown;
  location?: unknown;
  client?: unknown;
};

function relatedObject(value: unknown): Record<string, unknown> | null {
  const row = Array.isArray(value) ? value[0] : value;
  return row && typeof row === 'object' ? row as Record<string, unknown> : null;
}

async function hydrateShiftJobDetails(
  shifts: LaborScheduleShift[],
  supabase: SupabaseClient,
  loadedTickets: Record<string, unknown>[] = [],
): Promise<{ data: LaborScheduleShift[]; error: PostgrestError | null }> {
  const detailsByJob = new Map<string, NonNullable<LaborScheduleShift['jobDetails']>>();
  const installationCodeNames = new Map<string, { code: string | null; name: string | null; status: string | null }>();
  // The Schedule color each installation job or ticket gives its shifts.
  const colorByJob = new Map<string, string | null>();
  for (const kind of ['installation', 'service'] as const) {
    const installation = kind === 'installation';
    const loadedById = new Map(loadedTickets.map(row => [String(row.service_ticket_id), row]));
    const ids = [...new Set(shifts.map(shift => installation ? shift.installationJobId : shift.serviceTicketId)
      .filter((id): id is string => Boolean(id)))];
    const prefix = installation ? 'installation' : 'service';
    const idColumn = installation ? 'installation_job_id' : 'service_ticket_id';
    const addressColumns = ['address_line1', 'address_line2', 'city', 'state', 'postal_code', 'country']
      .map(column => `${prefix}_${column}`);
    const select = [idColumn, 'labor_calendar_color', ...addressColumns, installation
      ? 'job_code,name,status,site_location_id,installation_location_kind,venue_id,clients(business_name),client_branches(branch_name),venues(venue_name)'
      : 'location_id,service_location_name,client:clients!service_tickets_client_id_fkey(business_name)'].join(',');
    const missingIds = installation ? ids : ids.filter(id => !loadedById.has(id));
    const batches: Record<string, unknown>[][] = installation ? [] : [ids.flatMap(id => loadedById.has(id) ? [loadedById.get(id)!] : [])];
    const batchSize = installation ? 100 : 250;
    for (let offset = 0; offset < missingIds.length; offset += batchSize) {
      const batch = missingIds.slice(offset, offset + batchSize);
      const { data, error } = installation
        ? await supabase.from('installation_jobs').select(select).in(idColumn, batch).limit(batch.length)
        : await markRequest(supabase.rpc('get_service_ticket_schedule_details', { p_ticket_ids: batch }), 'read');
      if (error) return { data: [], error };
      batches.push((data ?? []) as Record<string, unknown>[]);
    }
    for (const batch of batches) {
      for (const value of batch) {
        const row = relatedObject(value);
        if (!row || !asNullableString(row[idColumn])) continue;
        const company = asNullableString(relatedObject(installation ? row.clients : row.client)?.business_name);
        const addressParts: JobAddress = { line1: asNullableString(row[`${prefix}_address_line1`]), line2: asNullableString(row[`${prefix}_address_line2`]),
          city: asNullableString(row[`${prefix}_city`]), state: asNullableString(row[`${prefix}_state`]),
          postalCode: asNullableString(row[`${prefix}_postal_code`]), country: asNullableString(row[`${prefix}_country`]) };
        const address = jobAddressLines(addressParts).join(', ') || null;
        const locationName = installation
          ? row.installation_location_kind === 'venue' ? asNullableString(relatedObject(row.venues)?.venue_name)
            : row.installation_location_kind === 'branch' ? asNullableString(relatedObject(row.client_branches)?.branch_name)
              : row.installation_location_kind === 'client' ? company : null
          : asNullableString(row.service_location_name);
        const venueId = installation && row.installation_location_kind === 'venue' ? asNullableString(row.venue_id) : null;
        detailsByJob.set(`${kind}:${row[idColumn]}`, { customerCompanyName: company, locationName, address, addressParts, venueId, itaLocationId: asNullableString(row[installation ? 'site_location_id' : 'location_id']) });
        if (installation) installationCodeNames.set(String(row[idColumn]), { code: asNullableString(row.job_code), name: asNullableString(row.name), status: asNullableString(row.status) });
        colorByJob.set(`${kind}:${row[idColumn]}`, asNullableString(row.labor_calendar_color));
      }
    }
  }
  return {
    data: shifts.map(shift => {
      const key = shift.installationJobId ? `installation:${shift.installationJobId}`
        : shift.serviceTicketId ? `service:${shift.serviceTicketId}` : null;
      const jobDetails = key ? detailsByJob.get(key) : undefined;
      // Recorded-only Installation time arrives without its job; name it, and so link it, like a visit.
      const installationJob = shift.installationJobId ? installationCodeNames.get(shift.installationJobId) : undefined;
      const job = !shift.eventCode ? installationJob : undefined;
      const coded = job?.code ? { ...shift, eventCode: job.code, eventName: job.name || shift.eventName } : shift;
      // Publishing a visit needs a confirmed installation; the header's Publish names the ones that are not.
      const named = installationJob ? { ...coded, installationJobStatus: installationJob.status } : coded;
      if (!key || !jobDetails) return named;
      const color = colorByJob.get(key) ?? null;
      return shift.installationJobId
        ? { ...named, jobDetails, installationCalendarColor: color }
        : { ...named, jobDetails, serviceTicketCalendarColor: color };
    }),
    error: null,
  };
}

async function hydrateShiftLocations(
  shifts: LaborScheduleShift[],
  supabase: SupabaseClient,
): Promise<{ data: LaborScheduleShift[]; error: PostgrestError | null }> {
  const locationId = (shift: LaborScheduleShift): string | null => {
    if (shift.installationJobId || shift.serviceTicketId) return shift.jobDetails?.itaLocationId ?? null;
    if (shift.events.length) return shift.events[0].siteLocationId;
    return shift.siteLocationId;
  };
  const ids = [...new Set(shifts.map(locationId).filter((id): id is string => Boolean(id)))];
  const byLocation = new Map<string, Pick<LaborScheduleShift, 'jobLocationName' | 'locationAddress' | 'locationVenues'>>();
  const addressParts = (row: Record<string, unknown>): JobAddress => ({
    line1: asNullableString(row.address_line1), line2: asNullableString(row.address_line2),
    city: asNullableString(row.city), state: asNullableString(row.state),
    postalCode: asNullableString(row.postal_code), country: asNullableString(row.country),
  });
  const address = (row: Record<string, unknown>) => jobAddressLines(addressParts(row)).join(', ') || null;
  // Only locations belonging to the already-authorized window, in bounded reads.
  for (let offset = 0; offset < ids.length; offset += 100) {
    const batch = ids.slice(offset, offset + 100);
    const { data, error } = await supabase.from('locations')
      .select('location_id,name,address_line1,address_line2,city,state,postal_code,country,location_venues(venue_id,is_default,venues(id,venue_name,address_line1,address_line2,city,state,postal_code,country,deleted_at))')
      .in('location_id', batch).limit(batch.length);
    if (error) return { data: [], error };
    for (const value of data ?? []) {
      const row = relatedObject(value);
      const id = asNullableString(row?.location_id);
      if (!row || !id) continue;
      const links = (Array.isArray(row.location_venues) ? row.location_venues : [])
        .map(relatedObject).filter((link): link is Record<string, unknown> => Boolean(link))
        .sort((a, b) => Number(b.is_default === true) - Number(a.is_default === true)
          || asString(a.venue_id).localeCompare(asString(b.venue_id)));
      const locationVenues = links.flatMap((link): LaborScheduleJobSite[] => {
        const venue = relatedObject(link.venues);
        const venueId = asNullableString(venue?.id);
        if (!venue || !venueId || venue.deleted_at != null) return [];
        const name = asNullableString(venue.venue_name) || 'Linked venue';
        return [{ id: venueId, venueId, label: name, locationName: name, address: address(venue), addressParts: addressParts(venue) }];
      });
      byLocation.set(id, { jobLocationName: asNullableString(row.name), locationAddress: address(row), locationVenues });
    }
  }
  return { data: shifts.map(shift => {
    const jobLocationId = locationId(shift);
    return { ...shift, jobLocationId, ...(jobLocationId ? byLocation.get(jobLocationId) : null) };
  }), error: null };
}

function localDateHourIso(value: string, hour: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), hour, 0, 0, 0);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function addHoursIso(value: string, hours: number): string | null {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  date.setHours(date.getHours() + hours);
  return date.toISOString();
}

function serviceTicketStartsAt(row: ServiceTicketScheduleRow): string | null {
  const scheduled = asNullableString(row.scheduled_start_at);
  if (scheduled) {
    const date = new Date(scheduled);
    return Number.isFinite(date.getTime()) ? date.toISOString() : null;
  }
  const requestedDate = normaliseEventQueryDate(row.requested_service_date);
  return requestedDate ? localDateHourIso(requestedDate, 9) : null;
}

function serviceTicketTitle(row: ServiceTicketScheduleRow): string {
  return asString(row.summary || row.doc_no || row.service_ticket_id) || 'External service ticket';
}

function serviceTicketDescription(row: ServiceTicketScheduleRow): string | null {
  const client = relatedObject(row.client);
  const parts = [
    asNullableString(row.doc_no),
    asNullableString(client?.business_name),
    asNullableString(row.service_room),
    // Problem reported can be formatted; the Schedule shows its text.
    asNullableString(flattenRichText(asNullableString(row.description))),
  ].filter((value): value is string => Boolean(value));
  return parts.length ? parts.join(' / ') : null;
}

function mapServiceTicketScheduleShift(row: ServiceTicketScheduleRow): LaborScheduleShift | null {
  const ticketId = asString(row.service_ticket_id);
  const startsAt = serviceTicketStartsAt(row);
  if (!ticketId || !startsAt) return null;
  const location = relatedObject(row.location);
  const locationName = asNullableString(location?.name) ?? asNullableString(row.service_location_name);
  const title = serviceTicketTitle(row);
  return {
    id: `service-ticket:${ticketId}`,
    eventId: null,
    eventName: title,
    eventCode: asNullableString(row.doc_no),
    events: [],
    eventRoomId: null,
    roomName: asNullableString(row.service_room),
    laborRoleId: null,
    roleName: 'Service Ticket',
    siteLocationId: asNullableString(row.location_id),
    locationName,
    startsAt,
    endsAt: addHoursIso(startsAt, 1),
    updatedAt: asNullableString(row.updated_at),
    requiredPeople: 1,
    scheduleStatus: asString(row.status) === 'open' ? 'planned' : 'scheduled',
    publishStatus: 'published',
    scheduleType: 'service',
    source: 'service_ticket',
    isOpen: false,
    isTentative: false,
    needsTime: false,
    notes: serviceTicketDescription(row),
    assignmentCount: 1,
    shortage: 0,
    conflictCount: 0,
    assignments: [],
    vehicleAssignments: [],
    serviceTicketId: ticketId,
  };
}

function shouldFetchServiceTicketScheduleRows(filters: LaborCalendarFilters): boolean {
  if (filters.scope !== 'team') return false;
  if (filters.myEventsOnly) return false;
  if (filters.eventId || filters.eventIds?.length) return false;
  if (filters.roleId || filters.roleIds?.length) return false;
  if (filters.personProfileId || filters.personProfileIds?.length) return false;
  if (filters.freelancerId || filters.freelancerIds?.length) return false;
  if (filters.assignmentStatus && filters.assignmentStatus !== 'all') return false;
  return !filters.publishStatus || filters.publishStatus === 'all' || filters.publishStatus === 'published';
}

async function fetchExternalServiceTicketScheduleRows(
  filters: LaborCalendarFilters,
  supabase: SupabaseClient,
): Promise<{ data: LaborScheduleShift[]; tickets: Record<string, unknown>[]; error: PostgrestError | null }> {
  if (!shouldFetchServiceTicketScheduleRows(filters)) return { data: [], tickets: [], error: null };
  const startYmd = normaliseEventQueryDate(filters.windowStart);
  const endYmd = normaliseEventQueryDate(filters.windowEnd);
  if (!startYmd || !endYmd) return { data: [], tickets: [], error: null };
  const tickets: Record<string, unknown>[] = [];
  let afterId: string | null = null;
  for (;;) {
    const { data, error } = await markRequest(supabase.rpc('get_service_ticket_schedule_rows', {
      p_window_start: filters.windowStart, p_window_end: filters.windowEnd,
      p_start_date: startYmd, p_end_date: endYmd,
      p_location_ids: filters.locationIds?.length ? filters.locationIds : null,
      p_after_id: afterId, p_limit: 250,
    }), 'read');
    if (error) return { data: [], tickets: [], error };
    const rows = Array.isArray(data) ? data as Record<string, unknown>[] : [];
    tickets.push(...rows);
    if (rows.length < 250) break;
    const next = asNullableString(rows[rows.length - 1]?.service_ticket_id);
    if (!next || next === afterId || tickets.length >= 10000) {
      return { data: [], tickets: [], error: { name: 'ScheduleReadLimit', message: 'Service schedule is too large to load safely. Narrow the date or location filter.', code: 'PORTAL_READ_LIMIT', details: '', hint: '' } };
    }
    afterId = next;
  }
  return { data: tickets.map(mapServiceTicketScheduleShift).filter((row): row is LaborScheduleShift => Boolean(row)).sort((a, b) => a.startsAt.localeCompare(b.startsAt)), tickets, error: null };
}

export async function fetchLaborCalendarWindow(
  filters: LaborCalendarFilters,
  options?: LaborCalendarWindowFetchOptions,
): Promise<{ data: LaborScheduleShift[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_window_snapshot', {
    p_window_start: filters.windowStart,
    p_window_end: filters.windowEnd,
    p_scope: filters.scope,
    p_location_ids: filters.locationIds?.length ? filters.locationIds : null,
    p_event_id: filters.eventId || null,
    p_role_id: filters.roleId || null,
    p_person_profile_id: filters.personProfileId || null,
    p_freelancer_id: filters.freelancerId || null,
    p_publish_status: filters.publishStatus && filters.publishStatus !== 'all' ? filters.publishStatus : null,
    p_assignment_status: filters.assignmentStatus && filters.assignmentStatus !== 'all' ? filters.assignmentStatus : null,
  }), 'read');
  if (error) return { data: [], error };
  if (!data || data.complete !== true || !Array.isArray(data.rows)) {
    return { data: [], error: { name: 'IncompleteSchedule', code: 'PORTAL_READ_INCOMPLETE', message: 'Schedule did not return a complete window. Try refreshing it.', details: '', hint: '' } };
  }
  const rows: Record<string, unknown>[] = data.rows;
  let mapped = rows
    // Sling imports are retained while the integration is paused. Filter before
    // mapping, which treats unknown/legacy sources as manual shifts.
    .filter(row => row.source !== 'sling')
    .map(mapLaborScheduleShift)
    .filter(row => row.id && row.scheduleStatus !== 'cancelled');
  if (options?.limitedFreelancerView) {
    // The break reader intersects the same published-own-shift boundary and
    // returns only unpaid-break metadata. Never enrich this restricted response
    // through internal event, fleet, Installation, Service or compensation reads.
    try {
      return { data: await hydrateShiftBreaks(mapped, filters.windowStart, filters.windowEnd, supabase), error: null };
    } catch (error) { return { data: [], error: error as PostgrestError }; }
  }
  mapped = await hydrateShiftEventVenues(mapped, supabase);
  mapped = await hydrateVehicleTrips(mapped, supabase);
  if (options?.hydrateEventDetails !== false) {
    mapped = await hydrateShiftEventDetails(mapped, supabase);
  }
  if (options?.filterLogisticsRequirement !== false) {
    mapped = await filterUnneededLogisticsShifts(mapped, supabase);
  }
  mapped = filterGeneratedLogisticsShiftsForScope(mapped, filters.scope);
  const ticketRows = await fetchExternalServiceTicketScheduleRows(filters, supabase);
  if (ticketRows.error) return { data: [], error: ticketRows.error };
  const installationRows = await fetchInstallationScheduleRows(filters, supabase, mapped);
  if (installationRows.error) return { data: [], error: installationRows.error };
  const scheduledServiceTicketIds = new Set(
    mapped
      .filter(row => row.source !== 'time_tracking')
      .map(row => row.serviceTicketId)
      .filter((ticketId): ticketId is string => Boolean(ticketId)),
  );
  const fallbackTicketRows = ticketRows.data.filter(row => !scheduledServiceTicketIds.has(row.serviceTicketId ?? ''));
  const rowsWithInstallations = mergeInstallationScheduleRows(mapped, installationRows.data);
  const mergedRows = filters.scope === 'logistics'
    ? mapped.filter(isLogisticsShiftLike)
    : [...rowsWithInstallations, ...fallbackTicketRows].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const jobRows = await hydrateShiftJobDetails(mergedRows, supabase, ticketRows.tickets);
  if (jobRows.error) return jobRows;
  const located = await hydrateShiftLocations(jobRows.data, supabase);
  if (located.error) return located;
  try {
    return { data: await hydrateShiftBreaks(located.data, filters.windowStart, filters.windowEnd, supabase), error: null };
  } catch (error) {
    return { data: [], error: error as PostgrestError };
  }
}

/** Hydrate canonical open entries through the same genuine job/address owner as recorded shifts. */
export async function fetchLaborScheduleOpenActuals(filters: LaborCalendarFilters, ownProfile: string | null): Promise<{ data: LaborScheduleShift[]; serverOffset: number }> {
  const supabase = getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_open_actuals', {
    p_window_start: filters.windowStart, p_window_end: filters.windowEnd,
  }), 'read');
  if (error) throw error;
  if (!data || !Array.isArray(data.entries) || !data.server_now) throw new Error('Open job time could not be loaded. Refresh the schedule.');
  const serverOffset = Date.parse(data.server_now) - Date.now();
  const entries = data.entries as Array<import('./scheduleTimeTracking').TimeEntry & { location_id: string; timezone: string }>;
  const mapped = entries.filter(entry => {
    if (filters.scope !== 'team' && filters.scope !== 'mine') return false;
    if (filters.scope === 'mine' && entry.profile_id !== ownProfile) return false;
    if (filters.locationIds?.length && !filters.locationIds.includes(entry.location_id)) return false;
    if (filters.publishStatus && !['all','published'].includes(filters.publishStatus)) return false;
    if (filters.assignmentStatus && !['all','assigned'].includes(filters.assignmentStatus)) return false;
    if (filters.personProfileId && filters.personProfileId !== entry.profile_id) return false;
    if (filters.personProfileIds?.length && !filters.personProfileIds.includes(entry.profile_id)) return false;
    if (filters.freelancerId || filters.freelancerIds?.length) return false;
    if (filters.eventId || filters.eventIds?.length || filters.roleId || filters.roleIds?.length) return false;
    return true;
  }).map(entry => ({ ...mapLaborScheduleShift({
    shift_id: entry.entry_id, source: 'time_tracking', time_entry_id: entry.entry_id,
    installation_job_id: entry.job_kind === 'installation' ? entry.job_id : null,
    service_ticket_id: entry.job_kind === 'service' ? entry.job_id : null,
    site_location_id: entry.location_id, event_name: entry.job_name, event_code: entry.job_code,
    starts_at: entry.clock_in, ends_at: null, unpaid_break_minutes: null, updated_at: entry.updated_at,
    required_people: 1, assignment_count: 1, schedule_status: 'confirmed', publish_status: 'published',
    schedule_type: entry.job_kind === 'service' ? 'service' : 'labor',
    shift_label: `${entry.job_code} · ${entry.job_name}`, notes: 'Unscheduled · Clocked in. Clock-out: TBD.',
    assignments: [{assignment_id: entry.entry_id, profile_id: entry.profile_id, assignment_type: 'employee', status: 'assigned', display_name: entry.person_name}],
  }), openActual: true, actualTimezone: entry.timezone }));
  const hydrated = await hydrateShiftJobDetails(mapped, supabase);
  if (hydrated.error) throw hydrated.error;
  const located = await hydrateShiftLocations(hydrated.data, supabase);
  if (located.error) throw located.error;
  return { data: located.data, serverOffset: Number.isFinite(serverOffset) ? serverOffset : 0 };
}

/** Someone clocked in on a job, matched to the planned shift their time will land on at clock-out. */
export type LaborScheduleActiveClock = {
  shiftId: string;
  profileId: string;
  clockIn: string;
  timezone: string | null;
};

/** Open clocks on shifts in the window, for the "On the clock" marker. Schedule managers only. */
export async function fetchLaborScheduleActiveClocks(
  windowStart: string,
  windowEnd: string,
  options?: ClientOption,
): Promise<{ data: LaborScheduleActiveClock[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_active_clocks', {
    p_window_start: windowStart,
    p_window_end: windowEnd,
  }), 'read');
  if (error) return { data: [], error };
  const clocks = (Array.isArray(data) ? data : []).flatMap((value): LaborScheduleActiveClock[] => {
    const row = relatedObject(value);
    const shiftId = asNullableString(row?.shift_id);
    const profileId = asNullableString(row?.profile_id);
    const clockIn = asNullableString(row?.clock_in);
    return shiftId && profileId && clockIn
      ? [{ shiftId, profileId, clockIn, timezone: asNullableString(row?.timezone) }]
      : [];
  });
  return { data: clocks, error: null };
}

export async function fetchLaborCalendarEventHoverDetails(
  eventIds: string[],
  options?: ClientOption,
): Promise<{ data: LaborScheduleEventLink[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const ids = Array.from(new Set(eventIds.map((id) => id.trim()).filter(Boolean)));
  if (ids.length === 0) {
    return { data: [], error: null };
  }

  const now = new Date().toISOString();
  const placeholderShifts: LaborScheduleShift[] = ids.map((eventId) => ({
    id: `hover:${eventId}`,
    eventId,
    eventName: '',
    eventCode: null,
    events: [
      {
        id: eventId,
        name: '',
        eventCode: null,
        siteLocationId: null,
        eventVenueId: null,
        venueId: null,
        venueName: null,
        laborCalendarColor: null,
        startDate: null,
        endDate: null,
        ...emptyEventHoverDetails(),
      },
    ],
    eventRoomId: null,
    roomName: null,
    laborRoleId: null,
    roleName: null,
    siteLocationId: null,
    locationName: null,
    startsAt: now,
    endsAt: null,
    updatedAt: null,
    requiredPeople: 0,
    scheduleStatus: 'planned',
    publishStatus: 'published',
    scheduleType: 'labor',
    source: 'manual',
    isOpen: false,
    isTentative: false,
    needsTime: false,
    notes: null,
    assignmentCount: 0,
    shortage: 0,
    conflictCount: 0,
    assignments: [],
    vehicleAssignments: [],
  }));

  const hydrated = await hydrateShiftEventDetails(placeholderShifts, supabase);
  const eventsById = new Map<string, LaborScheduleEventLink>();
  hydrated.forEach((shift) => {
    shift.events.forEach((event) => {
      eventsById.set(event.id, event);
    });
  });
  return { data: Array.from(eventsById.values()), error: null };
}

async function hydrateVehicleTrips(
  shifts: LaborScheduleShift[],
  supabase: SupabaseClient,
): Promise<LaborScheduleShift[]> {
  const shiftIds = shifts.map(shift => shift.id);
  if (!shiftIds.length) return shifts;

  const { data, error } = await markRequest(supabase.rpc('labor_schedule_vehicle_trips', {
    p_shift_ids: shiftIds,
  }), 'read');
  if (error) {
    if (isMissingOptionalLaborTable(error, 'labor_schedule_vehicle_trips')) return shifts;
    throw error;
  }

  const assignmentsByShift = new Map<string, LaborScheduleVehicleAssignment[]>();
  for (const assignment of normalizeVehicleAssignments(data)) {
    const current = assignmentsByShift.get(assignment.shiftId) ?? [];
    current.push(assignment);
    assignmentsByShift.set(assignment.shiftId, current);
  }
  return shifts.map(shift => {
    const vehicleAssignments = assignmentsByShift.get(shift.id);
    return vehicleAssignments ? { ...shift, vehicleAssignments } : shift;
  });
}

type HydratedEventDetails = LaborScheduleEventHoverDetails & {
  name: string | null;
  eventCode: string | null;
  eventVenueId: string | null;
  venueId: string | null;
  venueName: string | null;
  laborCalendarColor: string | null;
  startDate: string | null;
  endDate: string | null;
  primaryContactId: string | null;
  taxSubtotal: number;
};

type EventHydrationRow = {
  sales_person_profile_id?: unknown;
  sales_manager_id?: unknown;
  id?: unknown;
  name?: unknown;
  event_code?: unknown;
  client_business?: unknown;
  primary_contact_id?: unknown;
  payment_terms?: unknown;
  tax_subtotal?: unknown;
  labor_calendar_color?: unknown;
  event_start_date?: unknown;
  event_end_date?: unknown;
  show_start_date?: unknown;
  show_end_date?: unknown;
  clients?: EventHydrationClientRow | EventHydrationClientRow[] | null;
};

type EventHydrationClientRow = {
  business_name?: unknown;
  payment_terms?: unknown;
};

type PaymentTermHydrationRow = {
  payment_terms_id?: unknown;
  payment_terms_code?: unknown;
  description?: unknown;
};

type EventContactHydrationRow = {
  id?: unknown;
  first_name?: unknown;
  last_name?: unknown;
  email?: unknown;
};

type EventTotalHydrationRow = {
  event_id?: unknown;
  amount_post_discount?: unknown;
};

type LogisticsRequirementEventRow = {
  id?: unknown;
  site_location_id?: unknown;
  logistics_event_venue_id?: unknown;
};

type LogisticsRequirementLocationRow = {
  location_id?: unknown;
  name?: unknown;
  display_name?: unknown;
  address_line1?: unknown;
  city?: unknown;
  state?: unknown;
  postal_code?: unknown;
  country?: unknown;
};

type LogisticsRequirementEventVenueRow = {
  id?: unknown;
  event_id?: unknown;
  venue_id?: unknown;
  venue_name?: unknown;
  address_line1?: unknown;
  city?: unknown;
  state?: unknown;
  postal_code?: unknown;
  country?: unknown;
  position?: unknown;
  created_at?: unknown;
};

type LogisticsRequirementVenueRow = {
  id?: unknown;
  venue_name?: unknown;
  address_line1?: unknown;
  city?: unknown;
  state?: unknown;
  postal_code?: unknown;
  country?: unknown;
};

type LogisticsRequirementLocationVenueRow = {
  location_id?: unknown;
  venue_id?: unknown;
};

function formatContactName(row: EventContactHydrationRow): string | null {
  const name = [row.first_name, row.last_name]
    .map(value => asString(value).trim())
    .filter(Boolean)
    .join(' ');
  return name || asNullableString(row.email);
}

function normalizedEquipmentLineAction(value: unknown): string {
  const text = asString(value).trim().toLowerCase();
  return text || 'rent';
}

function eventLineEquipmentName(line: EventLineRecord): string | null {
  return asNullableString(
    line.description
      ?? line.products?.name_public
      ?? line.items?.name_internal
      ?? line.product_code
      ?? line.item_code,
  );
}

function isEventLineEquipment(line: EventLineRecord): boolean {
  const kind = asString(line.kind).trim().toLowerCase();
  if (kind === 'section' || kind === 'section_subtotal' || kind === 'labor') return false;
  const hasCatalogIdentity = Boolean(
    line.product_id
      || line.item_id
      || line.product_code
      || line.item_code
      || line.rollup_group_id,
  );
  return hasCatalogIdentity && Boolean(eventLineEquipmentName(line));
}

function eventLineRollupKey(line: EventLineRecord): string {
  const action = normalizedEquipmentLineAction(line.action);
  if (line.rollup_group_id) return `server:${line.rollup_group_id}:action:${action}`;
  const roomScope = asString(line.room_key ?? line.event_room_access_id);
  const kind = asString(line.kind);
  const productId = asString(line.product_id);
  const itemId = asString(line.item_id);
  const description = asString(line.description ?? eventLineEquipmentName(line));
  const note = asString(line.note);
  const kitInstance = asString(line.kit_instance_id);
  const componentId = asString(line.kit_component_id);
  const unitPrice = (asNumber(line.unit_price) || 0).toFixed(4);
  const discount = (asNumber(line.discount) || 0).toFixed(2);
  return `client:${roomScope}||${kind}||${productId}||${itemId}||${description}||${note}||${unitPrice}||${discount}||${action}||${kitInstance}||${componentId}`;
}

function summarizeEventEquipmentLines(lines: EventLineRecord[], limit = 4): string[] {
  type EquipmentBucket = {
    label: string;
    lineNumber: number;
    firstIndex: number;
    dateQuantities: Map<string, number>;
  };
  const buckets = new Map<string, EquipmentBucket>();
  lines.forEach((line, index) => {
    if (!isEventLineEquipment(line)) return;
    const label = eventLineEquipmentName(line);
    if (!label) return;
    const key = eventLineRollupKey(line);
    const lineNumber = asNullableNumber(line.line_number) ?? Number.POSITIVE_INFINITY;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        label,
        lineNumber,
        firstIndex: index,
        dateQuantities: new Map(),
      };
      buckets.set(key, bucket);
    } else {
      bucket.lineNumber = Math.min(bucket.lineNumber, lineNumber);
      bucket.firstIndex = Math.min(bucket.firstIndex, index);
    }
    const dateKey = asString(line.usage_room_date || 'undated').slice(0, 10) || 'undated';
    const qty = Math.max(0, asNumber(line.qty));
    bucket.dateQuantities.set(dateKey, (bucket.dateQuantities.get(dateKey) ?? 0) + qty);
  });

  const sortedBuckets = Array.from(buckets.values())
    .filter(bucket => Array.from(bucket.dateQuantities.values()).some(qty => qty > 0))
    .sort((a, b) => {
      if (a.lineNumber !== b.lineNumber) return a.lineNumber - b.lineNumber;
      if (a.firstIndex !== b.firstIndex) return a.firstIndex - b.firstIndex;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });

  const visible = sortedBuckets.slice(0, limit).map(bucket => {
    const maxQty = Math.max(1, ...Array.from(bucket.dateQuantities.values()));
    return maxQty > 1 ? `${maxQty}x ${bucket.label}` : bucket.label;
  });
  const overflow = sortedBuckets.length - visible.length;
  return overflow > 0 ? [...visible, `+${overflow} more`] : visible;
}

async function fetchEventEquipmentSummaries(
  eventIds: string[],
  supabase: SupabaseClient,
): Promise<Map<string, string[]>> {
  const summaries = new Map<string, string[]>();
  for (const eventId of eventIds) {
    const { lines, error } = await fetchEventLines({
      eventId,
      supabase,
      timeoutMs: 10000,
      retry: { maxRetries: 0 },
    });
    if (error) continue;
    summaries.set(eventId, summarizeEventEquipmentLines(lines));
  }
  return summaries;
}

function normalizeLogisticsComparisonText(value: unknown): string {
  return asString(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function normalizeLogisticsAddress(parts: unknown[]): string | null {
  const normalized = parts
    .map(normalizeLogisticsComparisonText)
    .filter(Boolean)
    .join('|');
  return normalized || null;
}

function selectedEventVenueForRequirement(
  eventId: string,
  logisticsEventVenueId: string | null,
  eventVenuesByEventId: Map<string, LogisticsRequirementEventVenueRow[]>,
): LogisticsRequirementEventVenueRow | null {
  const rows = [...(eventVenuesByEventId.get(eventId) ?? [])];
  if (!rows.length) return null;
  rows.sort((a, b) => {
    const aId = asString(a.id);
    const bId = asString(b.id);
    if (logisticsEventVenueId) {
      if (aId === logisticsEventVenueId && bId !== logisticsEventVenueId) return -1;
      if (bId === logisticsEventVenueId && aId !== logisticsEventVenueId) return 1;
    }
    const aPosition = asNullableNumber(a.position) ?? Number.POSITIVE_INFINITY;
    const bPosition = asNullableNumber(b.position) ?? Number.POSITIVE_INFINITY;
    if (aPosition !== bPosition) return aPosition - bPosition;
    return asString(a.created_at).localeCompare(asString(b.created_at));
  });
  return rows[0] ?? null;
}

function eventVenueMatchesLocation(
  location: LogisticsRequirementLocationRow | undefined,
  eventVenue: LogisticsRequirementEventVenueRow | null,
  venue: LogisticsRequirementVenueRow | undefined,
  linkedLocationVenueIds: Set<string>,
): boolean | null {
  if (!location || !eventVenue) return null;
  const locationId = asString(location.location_id);
  const venueId = asNullableString(eventVenue.venue_id);
  if (locationId && venueId && linkedLocationVenueIds.has(`${locationId}:${venueId}`)) return true;

  const locationAddress = normalizeLogisticsAddress([
    location.address_line1,
    location.city,
    location.state,
    location.postal_code,
    location.country,
  ]);
  const venueAddress = normalizeLogisticsAddress([
    eventVenue.address_line1 ?? venue?.address_line1,
    eventVenue.city ?? venue?.city,
    eventVenue.state ?? venue?.state,
    eventVenue.postal_code ?? venue?.postal_code,
    eventVenue.country ?? venue?.country,
  ]);
  if (locationAddress && venueAddress) return locationAddress === venueAddress;

  const locationNames = [
    normalizeLogisticsComparisonText(location.display_name),
    normalizeLogisticsComparisonText(location.name),
  ].filter(Boolean);
  const venueNames = [
    normalizeLogisticsComparisonText(eventVenue.venue_name),
    normalizeLogisticsComparisonText(venue?.venue_name),
  ].filter(Boolean);
  if (locationNames.length && venueNames.length) {
    return locationNames.some(name => venueNames.includes(name));
  }
  return null;
}

async function fetchSameSiteLogisticsEventIds(
  eventIds: string[],
  supabase: SupabaseClient,
): Promise<Set<string>> {
  const sameSiteEventIds = new Set<string>();
  if (!eventIds.length) return sameSiteEventIds;
  try {
    const { data: eventRows, error: eventError } = await supabase
      .from('events')
      .select('id,site_location_id,logistics_event_venue_id')
      .in('id', eventIds);
    if (eventError || !Array.isArray(eventRows)) return sameSiteEventIds;

    const events = (eventRows as LogisticsRequirementEventRow[])
      .map(row => ({
        id: asString(row.id),
        siteLocationId: asNullableString(row.site_location_id),
        logisticsEventVenueId: asNullableString(row.logistics_event_venue_id),
      }))
      .filter(row => row.id);
    if (!events.length) return sameSiteEventIds;

    const locationIds = Array.from(new Set(events
      .map(event => event.siteLocationId)
      .filter((value): value is string => Boolean(value))));
    const eventIdsForQuery = events.map(event => event.id);
    const [locationsRes, eventVenuesRes] = await Promise.all([
      locationIds.length
        ? supabase
            .from('locations')
            .select('location_id,name,display_name,address_line1,city,state,postal_code,country')
            .in('location_id', locationIds)
        : Promise.resolve({ data: [], error: null }),
      supabase
        .from('event_venues')
        .select('id,event_id,venue_id,venue_name,address_line1,city,state,postal_code,country,position,created_at')
        .in('event_id', eventIdsForQuery)
        .is('deleted_at', null),
    ]);
    if (locationsRes.error || eventVenuesRes.error) return sameSiteEventIds;

    const locationsById = new Map<string, LogisticsRequirementLocationRow>();
    for (const row of Array.isArray(locationsRes.data) ? locationsRes.data as LogisticsRequirementLocationRow[] : []) {
      const id = asString(row.location_id);
      if (id) locationsById.set(id, row);
    }

    const eventVenues = Array.isArray(eventVenuesRes.data)
      ? eventVenuesRes.data as LogisticsRequirementEventVenueRow[]
      : [];
    const eventVenuesByEventId = new Map<string, LogisticsRequirementEventVenueRow[]>();
    const venueIds = new Set<string>();
    for (const row of eventVenues) {
      const eventId = asString(row.event_id);
      if (!eventId) continue;
      const current = eventVenuesByEventId.get(eventId) ?? [];
      current.push(row);
      eventVenuesByEventId.set(eventId, current);
      const venueId = asNullableString(row.venue_id);
      if (venueId) venueIds.add(venueId);
    }

    const [venuesRes, locationVenuesRes] = await Promise.all([
      venueIds.size
        ? supabase
            .from('venues')
            .select('id,venue_name,address_line1,city,state,postal_code,country')
            .in('id', Array.from(venueIds))
        : Promise.resolve({ data: [], error: null }),
      locationIds.length && venueIds.size
        ? supabase
            .from('location_venues')
            .select('location_id,venue_id')
            .in('location_id', locationIds)
            .in('venue_id', Array.from(venueIds))
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (venuesRes.error || locationVenuesRes.error) return sameSiteEventIds;

    const venuesById = new Map<string, LogisticsRequirementVenueRow>();
    for (const row of Array.isArray(venuesRes.data) ? venuesRes.data as LogisticsRequirementVenueRow[] : []) {
      const id = asString(row.id);
      if (id) venuesById.set(id, row);
    }

    const linkedLocationVenueIds = new Set<string>();
    for (const row of Array.isArray(locationVenuesRes.data) ? locationVenuesRes.data as LogisticsRequirementLocationVenueRow[] : []) {
      const locationId = asString(row.location_id);
      const venueId = asString(row.venue_id);
      if (locationId && venueId) linkedLocationVenueIds.add(`${locationId}:${venueId}`);
    }

    for (const event of events) {
      const location = event.siteLocationId ? locationsById.get(event.siteLocationId) : undefined;
      const eventVenue = selectedEventVenueForRequirement(event.id, event.logisticsEventVenueId, eventVenuesByEventId);
      const venueId = eventVenue ? asNullableString(eventVenue.venue_id) : null;
      const venue = venueId ? venuesById.get(venueId) : undefined;
      if (eventVenueMatchesLocation(location, eventVenue, venue, linkedLocationVenueIds) === true) {
        sameSiteEventIds.add(event.id);
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[labor-calendar] same-site logistics fallback failed', err);
    }
  }
  return sameSiteEventIds;
}

function paymentTermLabel(row: PaymentTermHydrationRow): string | null {
  const code = asNullableString(row.payment_terms_code);
  const description = asNullableString(row.description);
  if (code && description) return `${code} - ${description}`;
  return code || description;
}

async function resolvePaymentTermLabels(
  rawValues: string[],
  supabase: SupabaseClient,
): Promise<Map<string, string>> {
  const labels = new Map<string, string>();
  const values = Array.from(new Set(rawValues.map(value => value.trim()).filter(Boolean)));
  if (!values.length) return labels;

  const uuidValues = values.filter(isUuidText);
  if (uuidValues.length) {
    const { data, error } = await supabase
      .from('payment_terms')
      .select('payment_terms_id,payment_terms_code,description')
      .in('payment_terms_id', uuidValues);
    if (!error && Array.isArray(data)) {
      for (const row of data as PaymentTermHydrationRow[]) {
        const id = asNullableString(row.payment_terms_id);
        const label = paymentTermLabel(row);
        if (id && label) labels.set(id, label);
      }
    }
  }

  const codeValues = values.filter(value => !isUuidText(value));
  if (codeValues.length) {
    const { data, error } = await supabase
      .from('payment_terms')
      .select('payment_terms_id,payment_terms_code,description')
      .in('payment_terms_code', codeValues);
    if (!error && Array.isArray(data)) {
      for (const row of data as PaymentTermHydrationRow[]) {
        const code = asNullableString(row.payment_terms_code);
        const label = paymentTermLabel(row);
        if (code && label) labels.set(code, label);
      }
    }
  }

  return labels;
}

function mergeEventDetails(
  event: LaborScheduleEventLink,
  details: HydratedEventDetails | undefined,
): LaborScheduleEventLink {
  if (!details) return event;
  return {
    ...event,
    name: event.name || details.name || 'Untitled event',
    eventCode: event.eventCode ?? details.eventCode,
    eventVenueId: event.eventVenueId ?? details.eventVenueId,
    venueId: event.venueId ?? details.venueId,
    venueName: event.venueName ?? details.venueName,
    laborCalendarColor: event.laborCalendarColor ?? details.laborCalendarColor,
    startDate: event.startDate ?? details.startDate,
    endDate: event.endDate ?? details.endDate,
    customerCompanyName: details.customerCompanyName,
    salesPersonName: details.salesPersonName,
    clientName: details.clientName,
    grandTotal: details.grandTotal,
    paymentTerms: details.paymentTerms,
    equipmentSummary: details.equipmentSummary,
    venueAddress: event.venueAddress ?? details.venueAddress,
  };
}

async function hydrateShiftEventVenues(
  shifts: LaborScheduleShift[],
  supabase: SupabaseClient,
): Promise<LaborScheduleShift[]> {
  const eventIds = Array.from(new Set(shifts.flatMap(shift => [
    shift.eventId,
    ...shift.events.map(event => event.id),
  ]).filter((value): value is string => Boolean(value))));
  if (!eventIds.length) return shifts;

  try {
    const venuesByEvent = await fetchPrimaryEventVenues(eventIds, supabase);
    if (venuesByEvent.size === 0) return shifts.map(shift => ({ ...shift, jobSites: [] }));
    const roomIds = [...new Set(shifts.map(shift => shift.eventRoomId).filter((id): id is string => Boolean(id)))];
    const venueByRoom = new Map<string, string>();
    for (let offset = 0; offset < roomIds.length; offset += 100) {
      const { data, error } = await supabase.from('event_rooms').select('id,event_venue_id').in('id', roomIds.slice(offset, offset + 100));
      if (error) throw error;
      for (const room of data ?? []) venueByRoom.set(room.id, room.event_venue_id);
    }
    return shifts.map(shift => ({
      ...shift,
      jobSites: shift.events.flatMap(event => {
        const sites = venuesByEvent.get(event.id)?.sites ?? [];
        const roomVenue = shift.eventRoomId ? venueByRoom.get(shift.eventRoomId) : null;
        return sites.filter(site => !shift.eventRoomId || (roomVenue != null && site.id === roomVenue)).map(site => ({
          ...site, label: shift.events.length > 1 ? `${event.eventCode || event.name} · ${site.label}` : site.label,
        }));
      }),
      events: shift.events.map(event => {
        const venue = venuesByEvent.get(event.id);
        if (!venue) return event;
        return {
          ...event,
          eventVenueId: event.eventVenueId ?? venue.eventVenueId,
          venueId: event.venueId ?? venue.venueId,
          venueName: event.venueName ?? venue.venueName,
          venueAddress: event.venueAddress ?? venue.venueAddress,
        };
      }),
    }));
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[labor-calendar] event venue hydration failed', err);
    }
    return shifts.map(shift => ({ ...shift, jobSites: [] }));
  }
}

async function hydrateShiftEventDetails(
  shifts: LaborScheduleShift[],
  supabase: SupabaseClient,
): Promise<LaborScheduleShift[]> {
  const eventIds = Array.from(new Set(shifts.flatMap(shift => [
    shift.eventId,
    ...shift.events.map(event => event.id),
  ]).filter((value): value is string => Boolean(value))));
  if (!eventIds.length) return shifts;

  try {
    const detailsByEvent = new Map<string, HydratedEventDetails>();
    const { data: eventRows, error: eventError } = await supabase
      .from('events')
      .select('id,name,event_code,client_business,primary_contact_id,payment_terms,tax_subtotal,labor_calendar_color,event_start_date,event_end_date,show_start_date,show_end_date,sales_person_profile_id,sales_manager_id,clients:client_id(business_name,payment_terms)')
      .in('id', eventIds);

    if (!eventError && Array.isArray(eventRows)) {
      // Event Basic Info stores the salesperson's auth id. Retain the imported
      // profile-id fallback, and resolve names even for staff hidden from Schedule.
      const authIds = [...new Set(eventRows.map(row => asNullableString(row.sales_manager_id)).filter((id): id is string => Boolean(id)))];
      const profileIds = [...new Set(eventRows.map(row => asNullableString(row.sales_person_profile_id)).filter((id): id is string => Boolean(id)))];
      const [byAuth, byProfile] = await Promise.all([
        authIds.length ? supabase.from('user_profiles').select('id,auth_user_id,full_name').in('auth_user_id', authIds) : { data: [] },
        profileIds.length ? supabase.from('user_profiles').select('id,auth_user_id,full_name').in('id', profileIds) : { data: [] },
      ]);
      const salesNamesByAuth = new Map((byAuth.data ?? []).map(row => [row.auth_user_id, asNullableString(row.full_name)]));
      const salesNamesByProfile = new Map((byProfile.data ?? []).map(row => [row.id, asNullableString(row.full_name)]));
      for (const row of eventRows as EventHydrationRow[]) {
        const eventId = asString(row.id);
        if (!eventId) continue;
        const clientRelation = Array.isArray(row.clients) ? row.clients[0] : row.clients;
        const clientBusiness = asNullableString(row.client_business ?? clientRelation?.business_name);
        const paymentTerms = asNullableString(row.payment_terms) ?? asNullableString(clientRelation?.payment_terms);
        detailsByEvent.set(eventId, {
          ...emptyEventHoverDetails(),
          name: asNullableString(row.name),
          eventCode: asNullableString(row.event_code),
          eventVenueId: null,
          venueId: null,
          venueName: null,
          laborCalendarColor: asNullableString(row.labor_calendar_color),
          startDate: normalizeEventStartDate(row),
          endDate: normalizeEventEndDate(row),
          customerCompanyName: clientBusiness,
          salesPersonName: asNullableString(row.sales_manager_id)
            ? salesNamesByAuth.get(asString(row.sales_manager_id)) ?? null
            : salesNamesByProfile.get(asString(row.sales_person_profile_id)) ?? null,
          paymentTerms,
          primaryContactId: asNullableString(row.primary_contact_id),
          taxSubtotal: asNumber(row.tax_subtotal),
        });
      }
    }

    const venuesByEvent = await fetchPrimaryEventVenues(eventIds, supabase);
    for (const [eventId, venue] of venuesByEvent.entries()) {
      const event = detailsByEvent.get(eventId);
      if (!event) continue;
      event.eventVenueId = venue.eventVenueId;
      event.venueId = venue.venueId;
      event.venueName = venue.venueName;
      event.venueAddress = venue.venueAddress;
    }

    const paymentTermValues = Array.from(detailsByEvent.values())
      .map(event => event.paymentTerms)
      .filter((value): value is string => Boolean(value));
    const paymentTermLabels = await resolvePaymentTermLabels(paymentTermValues, supabase);
    if (paymentTermLabels.size) {
      for (const event of detailsByEvent.values()) {
        const value = event.paymentTerms?.trim();
        if (value && paymentTermLabels.has(value)) {
          event.paymentTerms = paymentTermLabels.get(value) ?? event.paymentTerms;
        }
      }
    }

    const primaryContactIds = Array.from(new Set(Array.from(detailsByEvent.values())
      .map(event => event.primaryContactId)
      .filter((value): value is string => Boolean(value))));
    if (primaryContactIds.length) {
      const { data: contactRows, error: contactError } = await supabase
        .from('event_contacts')
        .select('id,first_name,last_name,email')
        .in('id', primaryContactIds);
      if (!contactError && Array.isArray(contactRows)) {
        const contactNames = new Map<string, string>();
        for (const row of contactRows as EventContactHydrationRow[]) {
          const contactId = asString(row.id);
          const contactName = formatContactName(row);
          if (contactId && contactName) contactNames.set(contactId, contactName);
        }
        for (const event of detailsByEvent.values()) {
          event.clientName = event.primaryContactId ? contactNames.get(event.primaryContactId) ?? null : null;
        }
      }
    }

    const { data: totalRows } = await markRequest(supabase.rpc('get_event_totals_batch', { _event_ids: eventIds }), 'read');
    if (Array.isArray(totalRows)) {
      for (const row of totalRows as EventTotalHydrationRow[]) {
        const eventId = asString(row.event_id);
        const event = eventId ? detailsByEvent.get(eventId) : undefined;
        if (!event) continue;
        const amountPostDiscount = asNullableNumber(row.amount_post_discount);
        event.grandTotal = amountPostDiscount == null ? null : amountPostDiscount + event.taxSubtotal;
      }
    }

    const equipmentByEvent = await fetchEventEquipmentSummaries(eventIds, supabase);
    for (const [eventId, event] of detailsByEvent.entries()) {
      event.equipmentSummary = equipmentByEvent.get(eventId) ?? [];
    }

    return shifts.map(shift => {
      const events = shift.events.map(event => mergeEventDetails(event, detailsByEvent.get(event.id)));
      const primaryDetails = shift.eventId ? detailsByEvent.get(shift.eventId) : undefined;
      return {
        ...shift,
        eventName: shift.eventName || primaryDetails?.name || events[0]?.name || 'Independent shift',
        eventCode: shift.eventCode ?? primaryDetails?.eventCode ?? events.find(event => event.eventCode)?.eventCode ?? null,
        events,
      };
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[labor-calendar] event hover hydration failed', err);
    }
    return shifts;
  }
}

function normalizeRevenueEvents(value: unknown): LaborCalendarRevenueEvent[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row: any) => ({
      eventId: asString(row.event_id ?? row.eventId),
      eventCode: asNullableString(row.event_code ?? row.eventCode),
      name: asString(row.name ?? row.event_name ?? row.eventName ?? row.event_code ?? row.eventCode) || 'Untitled event',
      status: asNullableString(row.status),
      siteLocationId: asNullableString(row.site_location_id ?? row.siteLocationId),
      locationName: asNullableString(row.location_name ?? row.locationName),
      startDate: normalizeEventStartDate(row),
      endDate: normalizeEventEndDate(row),
      basisDate: asNullableString(row.basis_date ?? row.basisDate),
      netRevenue: asNumber(row.net_revenue ?? row.netRevenue),
    }))
    .filter(row => row.eventId);
}

export function mapLaborCalendarL2RSummary(row: any): LaborCalendarL2RSummary {
  const laborPercent = row?.labor_percent == null ? null : asNumber(row.labor_percent);
  const goalLaborPercent = row?.goal_labor_percent == null ? null : asNumber(row.goal_labor_percent);
  return {
    estimatedWages: row?.estimated_wages == null ? null : asNumber(row.estimated_wages),
    overtimeCost: row?.overtime_cost == null ? null : asNumber(row.overtime_cost),
    scheduledMinutes: asNumber(row?.scheduled_minutes),
    overtimeMinutes: asNumber(row?.overtime_minutes),
    laborPercent,
    absenceCount: asNumber(row?.absence_count),
    shiftCount: asNumber(row?.shift_count),
    netRevenue: asNumber(row?.net_revenue),
    goalLaborPercent,
    revenueEvents: normalizeRevenueEvents(row?.revenue_events),
  };
}

export function laborCalendarL2RParams(filters: LaborCalendarFilters) {
  const summaryFilters = normalizeLaborCalendarL2RFilters(filters);
  return {
    p_window_start: summaryFilters.windowStart,
    p_window_end: summaryFilters.windowEnd,
    p_scope: summaryFilters.scope,
    p_location_ids: summaryFilters.locationIds?.length ? summaryFilters.locationIds : null,
    p_event_id: summaryFilters.eventId || null,
    p_event_ids: summaryFilters.eventIds?.length ? summaryFilters.eventIds : null,
    p_include_unconfirmed_events: summaryFilters.includeUnconfirmedEvents === true,
    p_role_id: null,
    p_person_profile_id: null,
    p_freelancer_id: null,
    p_publish_status: null,
    p_assignment_status: null,
    p_my_events_only: false,
    p_revenue_date_basis: summaryFilters.revenueDateBasis === 'end' ? 'end' : 'start',
  };
}

export async function fetchLaborCalendarStatistic(filters: LaborCalendarL2RFilters, metric = 'summary', options?: ClientOption): Promise<{ data: Record<string, any> | null; error: PostgrestError | null }> {
  return revisionedRead(options?.supabase ?? getSupabaseClient(), {
    eventId: filters.eventId, locationIds: filters.locationIds ?? [], windowStart: filters.windowStart, windowEnd: filters.windowEnd,
  }, `schedule-statistic:${metric}:${JSON.stringify(filters)}`, ['schedule', 'financial', 'reference'], async () => {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_stats', {
    p_metric: metric,
    p_request: {
      window_start: filters.windowStart, window_end: filters.windowEnd,
      location_ids: filters.locationIds ?? [],
      profile_ids: filters.personProfileIds ?? [], freelancer_ids: filters.freelancerIds ?? [],
      event_ids: filters.eventIds?.length ? filters.eventIds : filters.eventId ? [filters.eventId] : [],
      revenue_basis: filters.revenueDateBasis || 'start', include_unconfirmed: filters.includeUnconfirmedEvents === true,
    },
  }), 'read');
  return { data, error };
  });
}

/**
 * Stats follow sites (docs/schedule-access-and-pay.md). `denied` is true when this person sees stats
 * nowhere; the page hides them. `restricted` is the server's reason when they see stats elsewhere but
 * not for this view (a site outside their region, or none of their sites in view).
 */
export async function fetchLaborCalendarL2RSummary(filters: LaborCalendarFilters, options?: ClientOption): Promise<{ data: LaborCalendarL2RSummary | null; denied: boolean; restricted: string | null; error: PostgrestError | null }> {
  const { data, error } = await fetchLaborCalendarStatistic(filters, 'summary', options);
  if (error) return { data: null, denied: false, restricted: null, error };
  if (data?.access?.can_view_totals !== true) {
    return data?.access?.has_stat_sites === true
      ? { data: null, denied: false, restricted: data.access.restriction_message || 'Figures are hidden for this view.', error: null }
      : { data: null, denied: true, restricted: null, error: null };
  }
  if (!data.totals) return { data: null, denied: false, restricted: null, error: null };
  return {
    data: {
      ...mapLaborCalendarL2RSummary(data.totals),
      canViewDetails: data.access.can_view_details === true,
      canViewWageTotals: data.access.can_view_wage_totals === true,
      restriction: data.access.restriction_message ?? null,
    },
    denied: false,
    restricted: null,
    error: null,
  };
}

/** Where the signed-in person may change shifts: every site, or their own sites (docs/schedule-access-and-pay.md). */
export type LaborScheduleEditScope = { canEdit: boolean; allSites: boolean; locationIds: string[] };

export async function fetchLaborScheduleEditScope(options?: ClientOption): Promise<LaborScheduleEditScope> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_my_edit_scope'), 'read');
  if (error) throw error;
  return {
    canEdit: data?.can_edit === true,
    allSites: data?.all_sites === true,
    locationIds: Array.isArray(data?.location_ids) ? data.location_ids.filter((id: unknown): id is string => typeof id === 'string') : [],
  };
}

export async function fetchLaborCalendarRoles(options?: ClientOption): Promise<{
  data: LaborCalendarRole[];
  error: PostgrestError | null;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('labor_roles')
    .select('labor_role_id,name,code,active,installation_enabled')
    .order('name', { ascending: true });
  if (error) return { data: [], error };
  return {
    data: (Array.isArray(data) ? data : [])
      .map((row: any) => ({
        id: asString(row.labor_role_id),
        name: asString(row.name),
        code: asNullableString(row.code),
        active: row.active !== false,
        installationEnabled: row.installation_enabled === true,
      }))
      .filter(row => row.id && row.name),
    error: null,
  };
}

function isMissingOptionalLaborTable(error: PostgrestError | null | undefined, tableName: string): boolean {
  if (!error) return false;
  const haystack = [
    error.code,
    error.message,
    error.details,
    error.hint,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(tableName.toLowerCase())
    || haystack.includes('could not find the table')
    || haystack.includes('relation does not exist')
    || haystack.includes('schema cache');
}

function isMissingLaborCalendarEventsRpc(error: PostgrestError | null | undefined): boolean {
  if (!error) return false;
  const haystack = [error.code, error.message, error.details, error.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return error.code === '42883'
    || error.code === 'PGRST202'
    || (
      haystack.includes('labor_calendar_events_v1')
      && (
        haystack.includes('does not exist')
        || haystack.includes('could not find')
        || haystack.includes('schema cache')
      )
    );
}

function addRoleAssignmentCount(
  counts: Map<string, Record<string, number>>,
  resourceId: string,
  roleId: string,
  count: number,
): void {
  if (!resourceId || !roleId || count <= 0) return;
  const current = counts.get(resourceId) ?? {};
  current[roleId] = (current[roleId] ?? 0) + count;
  counts.set(resourceId, current);
}

export function isSchedulableLaborResource(resource: LaborScheduleResource): boolean {
  return Boolean(resource.personId && resource.title && !resource.inactive && !resource.hiddenFromSchedule);
}

type LaborCalendarResourceOptions = ClientOption & {
  windowStart?: string | null;
  windowEnd?: string | null;
  includeVehicles?: boolean;
  includeHiddenPeople?: boolean;
};

async function fetchLaborCalendarVehicleResourceRows(
  supabase: SupabaseClient,
  locationIds: string[] | null | undefined,
  options?: Pick<LaborCalendarResourceOptions, 'windowStart' | 'windowEnd'>,
) {
  if (options?.windowStart && options?.windowEnd) {
    const scopedResult = await markRequest((supabase.rpc as any)('labor_schedule_vehicle_resources_v2', {
      p_location_ids: locationIds?.length ? locationIds : null,
      p_window_start: options.windowStart,
      p_window_end: options.windowEnd,
    }), 'read');
    if (!scopedResult.error || !isMissingOptionalLaborTable(scopedResult.error, 'labor_schedule_vehicle_resources_v2')) {
      return scopedResult;
    }
  }
  return markRequest(supabase.rpc('labor_schedule_vehicle_resources', {
    p_location_ids: locationIds?.length ? locationIds : null,
  }), 'read');
}

function mapLaborCalendarVehicleResources(rows: unknown): LaborScheduleResource[] {
  return (Array.isArray(rows) ? rows : [])
    .map((row: any) => {
      const vehicleId = asString(row.vehicle_id);
      const locationId = asNullableString(row.location_id);
      return {
        id: asString(row.resource_id) || `vehicle:${vehicleId}`,
        title: asString(row.title) || 'Unnamed vehicle',
        personId: null,
        kind: 'vehicle' as const,
        detail: asNullableString(row.detail),
        homeLocationId: locationId,
        assignedLocationIds: locationId ? [locationId] : [],
        rolePreferenceIds: [],
        roleAssignmentCounts: {},
        vehicleId,
        vehicleType: asNullableString(row.vehicle_type),
        licensePlate: asNullableString(row.license_plate),
        capacity: asNullableString(row.capacity),
        inactive: false,
        hiddenFromSchedule: false,
      };
    })
    .filter((resource: LaborScheduleResource) => resource.id && resource.vehicleId && resource.title);
}

export async function fetchLaborCalendarVehicleResources(
  locationIdsOrOptions?: string[] | null | LaborCalendarResourceOptions,
  maybeOptions?: LaborCalendarResourceOptions,
): Promise<{ data: LaborScheduleResource[]; error: PostgrestError | null }> {
  const locationIds = Array.isArray(locationIdsOrOptions) || locationIdsOrOptions == null
    ? locationIdsOrOptions
    : null;
  const options = Array.isArray(locationIdsOrOptions) || locationIdsOrOptions == null
    ? maybeOptions
    : locationIdsOrOptions;
  const supabase = options?.supabase ?? getSupabaseClient();
  const result = await fetchLaborCalendarVehicleResourceRows(supabase, locationIds, options);
  if (result.error) return { data: [], error: result.error };
  return { data: mapLaborCalendarVehicleResources(result.data), error: null };
}

export async function fetchLaborCalendarResources(
  locationIdsOrOptions?: string[] | null | LaborCalendarResourceOptions,
  maybeOptions?: LaborCalendarResourceOptions,
): Promise<{
  data: LaborScheduleResource[];
  error: PostgrestError | null;
}> {
  const locationIds = Array.isArray(locationIdsOrOptions) || locationIdsOrOptions == null
    ? locationIdsOrOptions
    : null;
  const options = Array.isArray(locationIdsOrOptions) || locationIdsOrOptions == null
    ? maybeOptions
    : locationIdsOrOptions;
  const supabase = options?.supabase ?? getSupabaseClient();
  let employeeQuery = supabase
    .from('user_profiles')
    .select('id,full_name,email,is_archived,home_location_id,hide_from_labor_schedule,unpaid_lunch_minutes')
    .not('is_archived', 'is', true);
  // Explicit crew search can reveal hidden staff; the calendar still excludes them.
  if (!options?.includeHiddenPeople) employeeQuery = employeeQuery.not('hide_from_labor_schedule', 'is', true);
  const [employeesRes, freelancersRes, employeePrefsRes, vehicleResourcesRes] = await Promise.all([
    employeeQuery.order('full_name', { ascending: true }),
    supabase
      .from('freelancers')
      .select('freelancer_id,name,email,active,freelancer_role_preferences(labor_role_id)')
      .not('active', 'is', false)
      .order('name', { ascending: true }),
    supabase
      .from('user_profile_labor_role_preferences')
      .select('profile_id,labor_role_id'),
    options?.includeVehicles === false
      ? Promise.resolve({ data: [], error: null })
      : fetchLaborCalendarVehicleResourceRows(supabase, locationIds, options),
  ]);
  if (employeesRes.error) return { data: [], error: employeesRes.error };
  if (freelancersRes.error) return { data: [], error: freelancersRes.error };
  if (employeePrefsRes.error && !isMissingOptionalLaborTable(employeePrefsRes.error, 'user_profile_labor_role_preferences')) {
    return { data: [], error: employeePrefsRes.error };
  }
  if (vehicleResourcesRes.error && !isMissingOptionalLaborTable(vehicleResourcesRes.error, 'labor_schedule_vehicle_resources')) {
    return { data: [], error: vehicleResourcesRes.error };
  }

  const employeePreferenceMap = new Map<string, string[]>();
  for (const row of Array.isArray(employeePrefsRes.data) ? employeePrefsRes.data : []) {
    const profileId = asString((row as any).profile_id);
    const roleId = asString((row as any).labor_role_id);
    if (!profileId || !roleId) continue;
    const current = employeePreferenceMap.get(profileId) ?? [];
    current.push(roleId);
    employeePreferenceMap.set(profileId, current);
  }

  const employees: LaborScheduleResource[] = (Array.isArray(employeesRes.data) ? employeesRes.data : [])
    .map((row: any) => ({
      id: `employee:${row.id}`,
      title: asString(row.full_name || row.email) || 'Unnamed team member',
      personId: asString(row.id),
      kind: 'employee' as const,
      detail: asNullableString(row.email),
      homeLocationId: asNullableString(row.home_location_id),
      // Site-access assignments intentionally stay out of Schedule resource
      // identity. They authorize what a manager may see; home_location_id says
      // where an employee belongs in the staffing rows.
      assignedLocationIds: [],
      rolePreferenceIds: employeePreferenceMap.get(asString(row.id)) ?? [],
      roleAssignmentCounts: {},
      inactive: row.is_archived === true,
      hiddenFromSchedule: row.hide_from_labor_schedule === true,
      unpaidLunchMinutes: row.unpaid_lunch_minutes === 30 || row.unpaid_lunch_minutes === 60
        ? row.unpaid_lunch_minutes
        : 0,
    }))
    .filter(resource => isSchedulableLaborResource(options?.includeHiddenPeople ? { ...resource, hiddenFromSchedule: false } : resource));

  const freelancers: LaborScheduleResource[] = (Array.isArray(freelancersRes.data) ? freelancersRes.data : [])
    .map((row: any) => ({
      id: `freelancer:${row.freelancer_id}`,
      title: asString(row.name || row.email) || 'Unnamed freelancer',
      personId: asString(row.freelancer_id),
      kind: 'freelancer' as const,
      detail: asNullableString(row.email),
      homeLocationId: null,
      assignedLocationIds: [],
      rolePreferenceIds: Array.isArray(row.freelancer_role_preferences)
        ? row.freelancer_role_preferences.map((pref: any) => asString(pref.labor_role_id)).filter(Boolean)
        : [],
      roleAssignmentCounts: {},
      inactive: row.active === false,
      hiddenFromSchedule: false,
    }))
    .filter(isSchedulableLaborResource);

  const vehicles = mapLaborCalendarVehicleResources(vehicleResourcesRes.data);

  return {
    data: [
      { id: 'lane:unassigned', title: 'Unassigned', personId: null, kind: 'lane', detail: null, homeLocationId: null, assignedLocationIds: [], rolePreferenceIds: [], inactive: false, hiddenFromSchedule: false },
      { id: 'lane:open', title: 'Open Shifts', personId: null, kind: 'lane', detail: null, homeLocationId: null, assignedLocationIds: [], rolePreferenceIds: [], inactive: false, hiddenFromSchedule: false },
      // Shown for locations that take no shift requests; see the Schedule's staffing lanes.
      { id: 'lane:tentative', title: 'Tentative', personId: null, kind: 'lane', detail: null, homeLocationId: null, assignedLocationIds: [], rolePreferenceIds: [], inactive: false, hiddenFromSchedule: false },
      ...vehicles,
      ...employees,
      ...freelancers,
    ],
    error: null,
  };
}

export async function fetchLaborCalendarRoleAssignmentCounts(
  locationIdsOrOptions?: string[] | null | ClientOption,
  maybeOptions?: ClientOption,
): Promise<{
  data: LaborScheduleRoleAssignmentCountMap;
  error: PostgrestError | null;
}> {
  const locationIds = Array.isArray(locationIdsOrOptions) || locationIdsOrOptions == null
    ? locationIdsOrOptions
    : null;
  const options = Array.isArray(locationIdsOrOptions) || locationIdsOrOptions == null
    ? maybeOptions
    : locationIdsOrOptions;
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_role_assignment_counts_v2', {
    p_location_ids: locationIds?.length ? locationIds : null,
  }), 'read');
  if (error) return { data: {}, error };

  const counts = new Map<string, Record<string, number>>();
  for (const row of Array.isArray(data) ? data : []) {
    const roleId = asString((row as any).labor_role_id);
    const count = asNumber((row as any).assignment_count);
    if (!roleId || count <= 0) continue;
    const profileId = asNullableString((row as any).profile_id);
    const freelancerId = asNullableString((row as any).freelancer_id);
    if (profileId) addRoleAssignmentCount(counts, `employee:${profileId}`, roleId, count);
    if (freelancerId) addRoleAssignmentCount(counts, `freelancer:${freelancerId}`, roleId, count);
  }

  return { data: Object.fromEntries(counts), error: null };
}

export function mergeLaborCalendarRoleAssignmentCounts(
  resources: LaborScheduleResource[],
  counts: LaborScheduleRoleAssignmentCountMap | null | undefined,
): LaborScheduleResource[] {
  if (!counts || Object.keys(counts).length === 0) return resources;
  return resources.map(resource => {
    const roleAssignmentCounts = counts[resource.id];
    return roleAssignmentCounts ? { ...resource, roleAssignmentCounts } : resource;
  });
}

export async function fetchLaborCalendarEvents(
  locationIds?: string[] | null,
  range?: LaborCalendarEventQueryRange | null,
  options?: ClientOption,
): Promise<{ data: LaborCalendarEventOption[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const selectColumns = 'id,name,event_code,status,site_location_id,labor_calendar_color,event_start_date,event_end_date,show_start_date,show_end_date,project_manager_profile_id,sales_person_profile_id,sales_manager_id';
  const startYmd = normaliseEventQueryDate(range?.windowStart);
  const endYmd = normaliseEventQueryDate(range?.windowEnd);
  const queries = [];
  const applyLocationFilter = (query: any) => (
    locationIds?.length ? query.in('site_location_id', locationIds) : query
  );

  if (startYmd && endYmd) {
    queries.push(applyLocationFilter(supabase
      .rpc('labor_calendar_events_v1' as any, {
        p_window_start: startYmd,
        p_window_end: endYmd,
      })
      .select(selectColumns)
      .order('event_start_date', { ascending: true, nullsFirst: false })
      .limit(600)));
  }

  queries.push(applyLocationFilter(supabase
    .from('events')
    .select(selectColumns)
    .gte('event_start_date', recentEventLookbackStartYmd())
    .order('event_start_date', { ascending: false, nullsFirst: false })
    .limit(250)));

  let results = await Promise.all(queries);
  if (
    startYmd
    && endYmd
    && isMissingLaborCalendarEventsRpc(results[0]?.error ?? null)
  ) {
    const legacyWindowQueries = [
      applyLocationFilter(supabase
        .from('events')
        .select(selectColumns)
        .lte('event_start_date', endYmd)
        .gte('event_end_date', startYmd)
        .order('event_start_date', { ascending: true, nullsFirst: false })
        .limit(300)),
      applyLocationFilter(supabase
        .from('events')
        .select(selectColumns)
        .gte('event_start_date', startYmd)
        .lte('event_start_date', endYmd)
        .order('event_start_date', { ascending: true, nullsFirst: false })
        .limit(300)),
      applyLocationFilter(supabase
        .from('events')
        .select(selectColumns)
        .lte('show_start_date', endYmd)
        .gte('show_end_date', startYmd)
        .order('show_start_date', { ascending: true, nullsFirst: false })
        .limit(300)),
      applyLocationFilter(supabase
        .from('events')
        .select(selectColumns)
        .gte('show_start_date', startYmd)
        .lte('show_start_date', endYmd)
        .order('show_start_date', { ascending: true, nullsFirst: false })
        .limit(300)),
    ];
    const legacyWindowResults = await Promise.all(legacyWindowQueries);
    results = [...legacyWindowResults, ...results.slice(1)];
  }
  const error = results.find(result => result.error)?.error ?? null;
  if (error) return { data: [], error };
  const data = results.flatMap(result => Array.isArray(result.data) ? result.data : []);
  const rows = new Map<string, LaborCalendarEventOption>();
  for (const row of data) {
    const mapped = {
      id: asString(row.id),
      name: asString(row.name || row.event_code) || 'Untitled event',
      eventCode: asNullableString(row.event_code),
      siteLocationId: asNullableString(row.site_location_id),
      eventVenueId: null,
      venueId: null,
      venueName: null,
      venueAddress: null,
      laborCalendarColor: asNullableString(row.labor_calendar_color),
      status: asNullableString(row.status),
      startDate: asNullableString(row.event_start_date || row.show_start_date),
      endDate: asNullableString(row.event_end_date || row.show_end_date || row.event_start_date || row.show_start_date),
      projectManagerProfileId: asNullableString(row.project_manager_profile_id),
      salesPersonProfileId: asNullableString(row.sales_person_profile_id),
      salesManagerAuthId: asNullableString(row.sales_manager_id),
    };
    if (mapped.id) rows.set(mapped.id, mapped);
  }
  const venuesByEvent = await fetchPrimaryEventVenues(Array.from(rows.keys()), supabase);
  for (const [eventId, venue] of venuesByEvent.entries()) {
    const current = rows.get(eventId);
    if (current) rows.set(eventId, { ...current, ...venue });
  }
  return {
    data: Array.from(rows.values()),
    error: null,
  };
}

export async function fetchLaborCalendarRooms(
  eventIds?: string[] | null,
  options?: ClientOption,
): Promise<{ data: LaborCalendarRoomOption[]; error: PostgrestError | null }> {
  if (!eventIds?.length) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_rooms')
    .select('id,event_id,name,deleted_at')
    .in('event_id', eventIds)
    .is('deleted_at', null)
    .order('name', { ascending: true });
  if (error) return { data: [], error };
  return {
    data: (Array.isArray(data) ? data : [])
      .map((row: any) => ({
        id: asString(row.id),
        eventId: asString(row.event_id),
        name: asString(row.name) || 'Unnamed room',
      }))
      .filter(row => row.id && row.eventId),
    error: null,
  };
}

export async function fetchLaborTimeOffRequests(
  windowStart: string,
  windowEnd: string,
  options?: ClientOption,
): Promise<{ data: LaborCalendarTimeOffRequest[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('labor_schedule_time_off_requests')
    .select('time_off_request_id,profile_id,freelancer_id,site_location_id,starts_at,ends_at,status,reason,updated_at,is_all_day,time_zone')
    .lt('starts_at', windowEnd)
    .gt('ends_at', windowStart)
    .order('starts_at', { ascending: true });
  if (error) return { data: [], error };
  return {
    data: (Array.isArray(data) ? data : []).map((row: any) => ({
      id: asString(row.time_off_request_id),
      profileId: asNullableString(row.profile_id),
      freelancerId: asNullableString(row.freelancer_id),
      startsAt: asString(row.starts_at),
      endsAt: asString(row.ends_at),
      status: (row.status || 'requested') as LaborCalendarTimeOffRequest['status'],
      reason: asNullableString(row.reason),
      siteLocationId: asNullableString(row.site_location_id),
      updatedAt: asNullableString(row.updated_at),
      allDay: typeof row.is_all_day === 'boolean' ? row.is_all_day : null,
      timeZone: asNullableString(row.time_zone),
    })).filter(row => row.id),
    error: null,
  };
}

export async function createLaborScheduleShift(
  payload: {
    source?: 'manual' | 'installation';
    /** An independent visit on a ticket that already has one: source 'manual', type 'service'. */
    service_ticket_id?: string | null;
    schedule_type?: 'service';
    installation_job_id?: string | null;
    installation_room_id?: string | null;
    installation_labor_line_id?: string | null;
    event_id?: string | null;
    event_ids?: string[] | null;
    event_room_id?: string | null;
    labor_role_id?: string | null;
    site_location_id?: string | null;
    starts_at: string;
    ends_at?: string | null;
    required_people: number;
    schedule_status?: string;
    publish_status?: string;
    is_open?: boolean;
    is_tentative?: boolean;
    needs_time?: boolean;
    shift_label?: string | null;
    unpaid_break_minutes?: number | null;
    unpaid_breaks?: UnpaidBreak[] | null;
    lunch_break_minutes?: number | null;
    lunch_break_classification?: BreakClassification;
    recurrence_series_id?: string | null;
    recurrence_rule?: LaborScheduleRecurrenceRule | null;
    recurrence_index?: number | null;
    notes?: string | null;
    calendar_color?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: string | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { event_ids: eventIds, ...shiftPayload } = payload;
  const { data, error } = await supabase
    .from('labor_schedule_shifts')
    .insert(shiftPayload)
    .select('shift_id')
    .single();
  const shiftId = data?.shift_id ? String(data.shift_id) : null;
  if (error || !shiftId) return { data: shiftId, error: error ?? null };
  const linkError = await replaceLaborScheduleShiftEvents(supabase, shiftId, eventIds ?? (payload.event_id ? [payload.event_id] : []));
  return { data: shiftId, error: linkError };
}

export async function updateLaborScheduleShift(
  shiftId: string,
  patch: Record<string, unknown>,
  options?: ClientOption & {
    syncLogisticsShift?: LogisticsWarehouseShift | null;
    expectedUpdatedAt?: string | null;
  },
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { event_ids: eventIds, ...shiftPatch } = patch;
  if (options?.expectedUpdatedAt) {
    const { error } = await markRequest(supabase.rpc('labor_schedule_update_shift_v2', {
      p_shift_id: shiftId,
      p_patch: shiftPatch,
      p_event_ids: Array.isArray(eventIds) ? Array.from(new Set(eventIds.map(String).filter(Boolean))) : null,
      p_expected_updated_at: options.expectedUpdatedAt,
    }), 'mutation');
    return error ?? null;
  }
  const { error } = await supabase
    .from('labor_schedule_shifts')
    .update(shiftPatch)
    .eq('shift_id', shiftId);
  if (error) return error;
  if (Array.isArray(eventIds)) {
    const linkError = await replaceLaborScheduleShiftEvents(supabase, shiftId, eventIds.map(String));
    if (linkError) return linkError;
  }
  return syncEventWarehouseFieldsForLogisticsShift(
    options?.syncLogisticsShift,
    asNullableString(shiftPatch.starts_at) ?? options?.syncLogisticsShift?.startsAt ?? null,
    { supabase },
  );
}

async function replaceLaborScheduleShiftEvents(
  supabase: SupabaseClient,
  shiftId: string,
  eventIds: string[] | null | undefined,
): Promise<PostgrestError | null> {
  const uniqueEventIds = Array.from(new Set((eventIds ?? []).map(String).filter(Boolean)));
  const deleteRes = await supabase
    .from('labor_schedule_shift_events')
    .delete()
    .eq('shift_id', shiftId);
  if (deleteRes.error) return deleteRes.error;
  if (!uniqueEventIds.length) return null;
  const insertRes = await supabase
    .from('labor_schedule_shift_events')
    .insert(uniqueEventIds.map((eventId, index) => ({
      shift_id: shiftId,
      event_id: eventId,
      is_primary: index === 0,
    })));
  return insertRes.error ?? null;
}

export async function deleteLaborScheduleShift(
  shiftId: string,
  options?: ClientOption & {
    shift?: Pick<LaborScheduleShift, 'id' | 'source' | 'serviceTicketId'> | null;
    expectedUpdatedAt?: string | null;
  },
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const serviceTicketId = options?.shift?.source === 'service_ticket'
    ? options.shift.serviceTicketId ?? null
    : null;
  const isVirtualServiceTicket = options?.shift?.source === 'service_ticket' && shiftId.startsWith('service-ticket:');
  if (options?.expectedUpdatedAt && !isVirtualServiceTicket) {
    const { error } = await markRequest(supabase.rpc('labor_schedule_delete_shift_v2', {
      p_shift_id: shiftId,
      p_expected_updated_at: options.expectedUpdatedAt,
    }), 'mutation');
    return error ?? null;
  }
  if (serviceTicketId) {
    const { error: ticketError } = await supabase
      .from('service_tickets')
      .update({
        requested_service_date: null,
        requested_service_time_preference: 'anytime',
        requested_service_start_time: null,
        scheduled_start_at: null,
      })
      .eq('service_ticket_id', serviceTicketId);
    if (ticketError) return ticketError;
  }
  if (isVirtualServiceTicket) {
    return null;
  }
  const { error } = await supabase
    .from('labor_schedule_shifts')
    .delete()
    .eq('shift_id', shiftId);
  return error ?? null;
}

export async function generateLaborScheduleFromEvent(eventId: string, options?: ClientOption): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_generate_from_event_labor', { p_event_id: eventId }), 'mutation');
  if (error) throw error;
  return asNumber(data);
}

export async function ensureConfirmedLaborCalendarOpenShifts(
  filters: LaborCalendarFilters,
  options?: ClientOption,
): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_ensure_confirmed_open_shifts', {
    p_window_start: filters.windowStart,
    p_window_end: filters.windowEnd,
    p_location_ids: filters.locationIds?.length ? filters.locationIds : null,
    p_event_id: filters.eventId || null,
  }), 'mutation');
  if (error) throw error;
  return asNumber(data);
}

export async function ensureUnconfirmedLaborCalendarTentativeShifts(
  filters: LaborCalendarFilters,
  options?: ClientOption,
): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_ensure_unconfirmed_tentative_shifts', {
    p_window_start: filters.windowStart,
    p_window_end: filters.windowEnd,
    p_location_ids: filters.locationIds?.length ? filters.locationIds : null,
    p_event_id: filters.eventId || null,
    p_event_ids: filters.eventIds?.length ? filters.eventIds : null,
  }), 'mutation');
  if (error) throw error;
  return asNumber(data);
}

export async function moveLaborScheduleShift(payload: {
  shiftId: string;
  startsAt: string;
  endsAt?: string | null;
  resourceId?: string | null;
  shift?: LogisticsWarehouseShift | null;
  notifyAssignment?: boolean;
}, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_move_shift', {
    p_shift_id: payload.shiftId,
    p_starts_at: payload.startsAt,
    p_ends_at: payload.endsAt || null,
    p_resource_id: payload.resourceId || null,
  }), 'mutation');
  if (error) throw error;
  const syncError = await syncEventWarehouseFieldsForLogisticsShift(payload.shift, payload.startsAt, { supabase });
  if (syncError) throw syncError;
  if (payload.notifyAssignment) {
    const target = notificationTargetFromResourceId(payload.resourceId);
    if (target) {
      void notifyLaborCalendar({ type: 'assignment', shiftIds: [payload.shiftId], ...target });
    }
  }
  return asString(data);
}

export async function copyLaborScheduleShift(payload: {
  shiftId: string;
  startsAt: string;
  resourceId?: string | null;
  expectedUpdatedAt: string;
}, options?: ClientOption): Promise<LaborScheduleShift> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_copy_shift_v2', {
    p_shift_id: payload.shiftId,
    p_starts_at: payload.startsAt,
    p_resource_id: payload.resourceId ?? null,
    p_expected_updated_at: payload.expectedUpdatedAt,
  }), 'mutation');
  if (error) throw laborScheduleMutationError(error);
  if (!data || typeof data !== 'object' || !data.shift_id || !data.starts_at || !data.updated_at) {
    throw new Error('The copy response was incomplete. Reload the schedule before trying again.');
  }
  return mapLaborScheduleShift(data);
}

/** Pastes copied shifts in one transaction. A null resourceId keeps each source shift's own crew. */
export async function copyLaborScheduleShifts(copies: ReadonlyArray<{
  shiftId: string;
  startsAt: string;
  endsAt?: string | null;
  resourceId: string | null;
  expectedUpdatedAt: string;
}>, options?: ClientOption): Promise<LaborScheduleShift[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_copy_shifts', {
    p_copies: copies.map(copy => ({
      shift_id: copy.shiftId, starts_at: copy.startsAt, resource_id: copy.resourceId, updated_at: copy.expectedUpdatedAt,
      ...(copy.endsAt !== undefined ? { ends_at: copy.endsAt } : {}),
    })),
  }), 'mutation');
  if (error) throw laborScheduleMutationError(error);
  if (!Array.isArray(data) || data.length !== copies.length
    || data.some(row => !row || typeof row !== 'object' || !row.shift_id || !row.starts_at || !row.updated_at)) {
    throw new Error('The paste response was incomplete. Reload the schedule before trying again.');
  }
  return data.map(row => mapLaborScheduleShift(row));
}

export async function moveLaborScheduleShifts(
  moves: LaborScheduleMove[],
  options?: ClientOption,
): Promise<LaborScheduleMoveResult[]> {
  if (moves.length === 0) return [];
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_move_shifts_v3', {
    p_moves: moves.map(move => ({
      shift_id: move.shiftId,
      starts_at: move.startsAt,
      ends_at: move.endsAt ?? null,
      resource_id: move.resourceId ?? null,
      from_resource_id: move.fromResourceId ?? null,
      expected_updated_at: move.expectedUpdatedAt,
    })),
  }), 'mutation');
  if (error) throw laborScheduleMutationError(error);

  const saved: LaborScheduleMoveResult[] = (Array.isArray(data) ? data : []).flatMap(row => {
    if (!row?.shift_id || !row.updated_at || !row.state?.starts_at || !Array.isArray(row.state.assignments)) return [];
    const state = mapLaborScheduleShift(row.state);
    const assignmentCount = state.assignments.filter(assignment => ['assigned', 'accepted', 'offered', 'requested'].includes(assignment.status)).length;
    return [{
      shiftId: asString(row.shift_id), updatedAt: asString(row.updated_at),
      state: {
        startsAt: state.startsAt, endsAt: state.endsAt, isOpen: state.isOpen, isTentative: state.isTentative, needsTime: state.needsTime,
        requiredPeople: state.requiredPeople, scheduleStatus: state.scheduleStatus, publishStatus: state.publishStatus,
        assignments: state.assignments, assignmentCount, shortage: Math.max(0, state.requiredPeople - assignmentCount),
      },
    }];
  });
  if (saved.length !== moves.length || new Set(saved.map(row => row.shiftId)).size !== moves.length
    || saved.some(row => !moves.some(move => move.shiftId === row.shiftId))) {
    throw new Error('The move response was incomplete. Reload the schedule before trying again.');
  }

  const profileIds = new Set<string>();
  const freelancerIds = new Set<string>();
  for (const move of moves) {
    if (!move.notifyAssignment) continue;
    const target = notificationTargetFromResourceId(move.resourceId);
    for (const id of target?.profileIds ?? []) profileIds.add(id);
    for (const id of target?.freelancerIds ?? []) freelancerIds.add(id);
  }
  if (profileIds.size || freelancerIds.size) {
    void notifyLaborCalendar({
      type: 'assignment',
      shiftIds: moves.map(move => move.shiftId),
      profileIds: Array.from(profileIds),
      freelancerIds: Array.from(freelancerIds),
    });
  }

  return saved;
}

export async function notifyLaborCalendar(payload: Record<string, unknown>): Promise<void> {
  try {
    const res = await apiFetch('/labor-calendar/notify', {
      method: 'POST',
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Notification failed (${res.status})`);
    }
  } catch (err) {
    // Email is best-effort. In-app notifications are inserted by the scheduling RPCs.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[labor-calendar] email notification failed', err);
    }
  }
}

function notificationTargetFromResourceId(resourceId: string | null | undefined): { profileIds?: string[]; freelancerIds?: string[] } | null {
  if (!resourceId) return null;
  if (resourceId.startsWith('employee:')) {
    const profileId = resourceId.slice('employee:'.length);
    return profileId ? { profileIds: [profileId] } : null;
  }
  if (resourceId.startsWith('freelancer:')) {
    const freelancerId = resourceId.slice('freelancer:'.length);
    return freelancerId ? { freelancerIds: [freelancerId] } : null;
  }
  return null;
}

export async function publishLaborSchedule(shiftIds: string[], options?: ClientOption): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_publish', { p_shift_ids: shiftIds }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'published', shiftIds });
  return asNumber(data);
}

/**
 * Statuses of events linked to shifts in view that the Schedule's event list did not load (another
 * site, outside the window or past its row limit). Publishing refuses drafts on unconfirmed events.
 */
export async function fetchLaborCalendarEventStatuses(eventIds: readonly string[], options?: ClientOption): Promise<Record<string, string | null>> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const statuses: Record<string, string | null> = {};
  for (let offset = 0; offset < eventIds.length; offset += 200) {
    const batch = eventIds.slice(offset, offset + 200);
    const { data, error } = await markRequest(supabase.from('events').select('id,status').in('id', batch).limit(batch.length), 'read');
    if (error) throw error;
    for (const row of (data ?? []) as Array<{ id: string; status: string | null }>) statuses[row.id] = row.status ?? null;
  }
  return statuses;
}

/** One person's access to one of the asked sites: `resourceId` is `employee:<profile id>`. */
export type LaborCalendarSiteAccess = { resourceId: string; locationId: string };

/**
 * Who can work at these sites beyond their home site, for the People filter's "With access" group: a
 * site assignment (user_profile_locations) or an all-sites grant on the site's region
 * (user_profile_regions.grant_all_locations), the rules labor_schedule_user_can_access_location uses.
 * Home sites come from the people already loaded. Internal staff can read all three tables.
 */
export async function fetchLaborCalendarSiteAccess(locationIds: readonly string[], options?: ClientOption): Promise<LaborCalendarSiteAccess[]> {
  if (!locationIds.length) return [];
  const supabase = options?.supabase ?? getSupabaseClient();
  const ids = [...locationIds];
  const pageSize = 1000;
  const rows: LaborCalendarSiteAccess[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await markRequest(supabase.from('user_profile_locations').select('user_profile_id,location_id')
      .in('location_id', ids).order('user_profile_id').order('location_id').range(from, from + pageSize - 1), 'read');
    if (error) throw error;
    const page = (data ?? []) as Array<{ user_profile_id: string; location_id: string }>;
    page.forEach(row => rows.push({ resourceId: `employee:${row.user_profile_id}`, locationId: row.location_id }));
    if (page.length < pageSize) break;
  }
  const { data: siteRows, error: siteError } = await markRequest(supabase.from('locations').select('location_id,region_id')
    .in('location_id', ids).limit(ids.length), 'read');
  if (siteError) throw siteError;
  const sitesByRegion = new Map<string, string[]>();
  for (const site of (siteRows ?? []) as Array<{ location_id: string; region_id: string | null }>) {
    if (!site.region_id) continue;
    sitesByRegion.set(site.region_id, [...(sitesByRegion.get(site.region_id) ?? []), site.location_id]);
  }
  if (!sitesByRegion.size) return rows;
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await markRequest(supabase.from('user_profile_regions').select('user_profile_id,region_id')
      .eq('grant_all_locations', true).in('region_id', [...sitesByRegion.keys()])
      .order('user_profile_id').order('region_id').range(from, from + pageSize - 1), 'read');
    if (error) throw error;
    const page = (data ?? []) as Array<{ user_profile_id: string; region_id: string }>;
    page.forEach(row => (sitesByRegion.get(row.region_id) ?? []).forEach(locationId => (
      rows.push({ resourceId: `employee:${row.user_profile_id}`, locationId })
    )));
    if (page.length < pageSize) break;
  }
  return rows;
}

/**
 * The header's Publish / Unpublish: one all-or-nothing change for the shifts the page showed.
 * The server refuses the whole set when any shift changed since its `updatedAt`.
 */
export async function setLaborSchedulePublishStatus(
  shifts: ReadonlyArray<Pick<LaborScheduleShift, 'id' | 'updatedAt'>>,
  publishStatus: 'published' | 'draft',
  options?: ClientOption,
): Promise<string[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_set_publish_status', {
    p_shifts: shifts.map(shift => ({ shift_id: shift.id, updated_at: shift.updatedAt ?? null })),
    p_publish_status: publishStatus,
  }), 'mutation');
  if (error) throw error;
  const ids = Array.isArray(data) ? data.map(String) : [];
  // Email, like the drawer's Publish; in-app notices come from labor_schedule_publish. Unpublishing tells no one.
  if (publishStatus === 'published' && ids.length) void notifyLaborCalendar({ type: 'published', shiftIds: ids });
  return ids;
}

export async function assignLaborScheduleShift(payload: {
  shiftId: string;
  profileId?: string | null;
  freelancerId?: string | null;
  status?: string;
  costRate?: number | null;
}, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_assign', {
    p_shift_id: payload.shiftId,
    p_profile_id: payload.profileId || null,
    p_freelancer_id: payload.freelancerId || null,
    p_status: payload.status || 'assigned',
    p_cost_rate: payload.costRate ?? null,
  }), 'mutation');
  if (error) throw error;
  const assignmentId = asString(data);
  void notifyLaborCalendar({ type: 'assignment', assignmentIds: assignmentId ? [assignmentId] : [] });
  return assignmentId;
}

export async function setLaborScheduleAssignments(payload: {
  shiftId: string;
  assignments: Array<{
    profileId?: string | null;
    freelancerId?: string | null;
    status?: string;
  }>;
}, options?: ClientOption): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const assignments = payload.assignments.map(assignment => ({
    profile_id: assignment.profileId || null,
    freelancer_id: assignment.freelancerId || null,
    status: assignment.status || 'assigned',
  }));
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_set_assignments', {
    p_shift_id: payload.shiftId,
    p_assignments: assignments,
  }), 'mutation');
  if (error) throw error;

  const profileIds = assignments.flatMap(assignment => assignment.profile_id ? [assignment.profile_id] : []);
  const freelancerIds = assignments.flatMap(assignment => assignment.freelancer_id ? [assignment.freelancer_id] : []);
  if (profileIds.length || freelancerIds.length) {
    void notifyLaborCalendar({
      type: 'assignment',
      shiftIds: [payload.shiftId],
      profileIds,
      freelancerIds,
    });
  }
  return asNumber(data);
}

export async function assignLaborScheduleVehicle(payload: {
  shiftId: string;
  vehicleId?: string | null;
}, options?: ClientOption): Promise<string | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_assign_vehicle', {
    p_shift_id: payload.shiftId,
    p_vehicle_id: payload.vehicleId || null,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'vehicle_assignment', shiftIds: [payload.shiftId] });
  return data == null ? null : asString(data);
}

export async function saveLaborScheduleVehicleTrip(payload: {
  shiftId: string;
  assignmentId?: string | null;
  vehicleId: string;
  loadStartsAt: string;
  returnAt: string;
  notes?: string | null;
  driverProfileId?: string | null;
  driverFreelancerId?: string | null;
  transitMinutes?: number | null;
  shift?: LogisticsWarehouseShift | null;
}, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_save_vehicle_trip', {
    p_shift_id: payload.shiftId,
    p_assignment_id: payload.assignmentId || null,
    p_vehicle_id: payload.vehicleId,
    p_load_starts_at: payload.loadStartsAt,
    p_return_at: payload.returnAt,
    p_notes: payload.notes || null,
    p_driver_profile_id: payload.driverProfileId || null,
    p_driver_freelancer_id: payload.driverFreelancerId || null,
    p_transit_minutes: payload.transitMinutes ?? null,
  }), 'mutation');
  if (error) throw error;
  const syncError = await syncEventWarehouseFieldsForLogisticsShift(payload.shift, payload.loadStartsAt, { supabase });
  if (syncError) throw syncError;
  void notifyLaborCalendar({ type: 'vehicle_trip', shiftIds: [payload.shiftId] });
  return asString(data);
}

export async function fetchLaborScheduleEventVehicleTrips(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: LaborScheduleEventVehicleTrip[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_event_vehicle_trips', {
    p_event_id: eventId,
  }), 'read');
  if (error) {
    if (isMissingOptionalLaborTable(error, 'labor_schedule_event_vehicle_trips')) return { data: [], error: null };
    return { data: [], error };
  }
  return { data: normalizeEventVehicleTrips(data), error: null };
}

export async function removeLaborScheduleVehicleTrip(
  assignmentId: string,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_remove_vehicle_trip', {
    p_assignment_id: assignmentId,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'vehicle_trip_removed', assignmentIds: [assignmentId] });
  return asString(data);
}

export async function requestOpenLaborShift(shiftId: string, notes?: string | null, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_request_open_shift', {
    p_shift_id: shiftId,
    p_notes: notes || null,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'open_request', shiftIds: [shiftId] });
  return asString(data);
}

export async function reviewLaborAssignmentRequest(
  assignmentId: string,
  status: 'assigned' | 'accepted' | 'declined' | 'cancelled',
  notes?: string | null,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_review_assignment_request', {
    p_assignment_id: assignmentId,
    p_status: status,
    p_manager_notes: notes || null,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'assignment_review', assignmentIds: [assignmentId] });
  return asString(data);
}

export async function reviewLaborSwapRequest(
  swapRequestId: string,
  status: 'approved' | 'rejected' | 'cancelled',
  notes?: string | null,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_review_swap', {
    p_swap_request_id: swapRequestId,
    p_status: status,
    p_manager_notes: notes || null,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'swap_review', swapRequestIds: [swapRequestId] });
  return asString(data);
}

export async function requestLaborSwap(
  shiftId: string,
  assignmentId?: string | null,
  notes?: string | null,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_request_swap', {
    p_shift_id: shiftId,
    p_assignment_id: assignmentId || null,
    p_notes: notes || null,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'swap_request', shiftIds: [shiftId] });
  return asString(data);
}

export type LaborTimeOffBookableProfile = {
  profileId: string;
  fullName: string;
  email: string;
  isSelf: boolean;
};

/**
 * Profiles the signed-in user may book time off for: themselves, anyone if they
 * are an admin, and their own reports if they are a manager. Sourced from the
 * database so the picker cannot offer someone the RPC would then reject.
 */
export async function fetchLaborTimeOffBookableProfiles(
  options?: ClientOption,
): Promise<LaborTimeOffBookableProfile[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_time_off_bookable_profiles'), 'read');
  if (error) throw error;
  return (Array.isArray(data) ? data : []).map((row: any) => ({
    profileId: asString(row?.profile_id),
    fullName: asString(row?.full_name),
    email: asString(row?.email),
    isSelf: Boolean(row?.is_self),
  })).filter(entry => entry.profileId);
}

export async function requestLaborTimeOff(
  startsAt: string,
  endsAt: string,
  locationId?: string | null,
  reason?: string | null,
  options?: ClientOption & { targetProfileId?: string | null; allDay?: boolean; timeZone?: string },
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const targetProfileId = options?.targetProfileId ?? null;
  const explicitInterval = typeof options?.allDay === 'boolean' && Boolean(options.timeZone);
  const { data, error } = await markRequest(supabase.rpc(explicitInterval ? 'labor_schedule_request_time_off_interval' : 'labor_schedule_request_time_off', {
    p_starts_at: startsAt,
    p_ends_at: endsAt,
    p_location_id: locationId || null,
    p_reason: reason || null,
    p_target_profile_id: targetProfileId,
    ...(explicitInterval ? { p_all_day: options?.allDay, p_calendar_time_zone: options?.timeZone } : {}),
  }), 'mutation');
  if (error) throw error;
  const requestId = asString(data);
  void notifyLaborCalendar({ type: 'time_off_request', timeOffRequestIds: [requestId] });
  return requestId;
}

export async function updateLaborTimeOffRequest(request: LaborCalendarTimeOffRequest, startsAt: string, endsAt: string, reason: string, allDay: boolean, timeZone: string): Promise<string> {
  if (!request.updatedAt) throw new Error('Reload this absence before editing it.');
  const { data, error } = await markRequest(getSupabaseClient().rpc('labor_schedule_update_time_off_interval', {
    p_time_off_request_id: request.id, p_starts_at: startsAt, p_ends_at: endsAt, p_reason: reason,
    p_all_day: allDay, p_calendar_time_zone: timeZone, p_expected_updated_at: request.updatedAt,
  }), 'mutation');
  if (error) throw error;
  if (data?.changed) void notifyLaborCalendar({ type: 'time_off_update', timeOffRequestIds: [request.id] });
  return asString(data?.time_off_request_id);
}

export async function cancelLaborTimeOffRequest(request: LaborCalendarTimeOffRequest): Promise<string> {
  if (!request.updatedAt) throw new Error('Reload this absence before cancelling it.');
  const { data, error } = await markRequest(getSupabaseClient().rpc('labor_schedule_cancel_time_off', {
    p_time_off_request_id: request.id, p_expected_updated_at: request.updatedAt,
  }), 'mutation');
  if (error) throw error;
  if (data?.changed) void notifyLaborCalendar({ type: 'time_off_cancel', timeOffRequestIds: [request.id] });
  return asString(data?.time_off_request_id);
}

export async function reviewLaborTimeOffRequest(
  timeOffRequestId: string,
  status: 'approved' | 'rejected' | 'cancelled',
  notes?: string | null,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_review_time_off', {
    p_time_off_request_id: timeOffRequestId,
    p_status: status,
    p_manager_notes: notes || null,
  }), 'mutation');
  if (error) throw error;
  void notifyLaborCalendar({ type: 'time_off_review', timeOffRequestIds: [timeOffRequestId] });
  return asString(data);
}

export async function copyLaborScheduleRange(
  sourceStart: string,
  sourceEnd: string,
  targetStart: string,
  locationIds?: string[] | null,
  options?: ClientOption,
): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('labor_schedule_copy_range', {
    p_source_start: sourceStart,
    p_source_end: sourceEnd,
    p_target_start: targetStart,
    p_location_ids: locationIds?.length ? locationIds : null,
  }), 'mutation');
  if (error) throw error;
  return asNumber(data);
}

function windowsOverlap(aStart: string, aEnd: string | null, bStart: string, bEnd: string | null): boolean {
  const startA = new Date(aStart).getTime();
  const endA = new Date(aEnd || aStart).getTime();
  const startB = new Date(bStart).getTime();
  const endB = new Date(bEnd || bStart).getTime();
  if (![startA, endA, startB, endB].every(Number.isFinite)) return false;
  const effectiveEndA = endA > startA ? endA : startA + 60 * 60 * 1000;
  const effectiveEndB = endB > startB ? endB : startB + 60 * 60 * 1000;
  return startA < effectiveEndB && startB < effectiveEndA;
}

function assignmentMatchesResource(assignment: LaborScheduleAssignment, resource: LaborScheduleResource): boolean {
  if (resource.kind === 'employee') return assignment.profileId === resource.personId;
  if (resource.kind === 'freelancer') return assignment.freelancerId === resource.personId;
  return false;
}

function visibleRoleAssignmentCount(
  resource: LaborScheduleResource,
  roleId: string,
  visibleShifts: LaborScheduleShift[],
): number {
  return visibleShifts.reduce((count, shift) => {
    if (shift.laborRoleId !== roleId) return count;
    const hasAssignment = shift.assignments.some(assignment => (
      ['assigned', 'accepted'].includes(assignment.status)
      && assignmentMatchesResource(assignment, resource)
    ));
    return hasAssignment ? count + 1 : count;
  }, 0);
}

export function rankLaborCandidates(
  shift: Pick<LaborScheduleShift, 'startsAt' | 'endsAt' | 'laborRoleId' | 'siteLocationId'>
    & Partial<Pick<LaborScheduleShift, 'id' | 'assignments'>>,
  resources: LaborScheduleResource[],
  visibleShifts: LaborScheduleShift[],
  timeOffRequests: LaborCalendarTimeOffRequest[],
): LaborCandidateSuggestion[] {
  return resources
    .filter(resource => (
      (resource.kind === 'employee' || resource.kind === 'freelancer')
      && resource.personId
      && !resource.inactive
      && !resource.hiddenFromSchedule
    ))
    .filter(resource => !(shift.assignments ?? []).some(assignment => (
      !['declined', 'cancelled', 'withdrawn'].includes(assignment.status)
      && assignmentMatchesResource(assignment, resource)
    )))
    .map(resource => {
      let score = 50;
      const reasons: string[] = [];
      let blocked = false;
      const roleAssignmentCount = shift.laborRoleId
        ? Math.max(
            resource.roleAssignmentCounts?.[shift.laborRoleId] ?? 0,
            visibleRoleAssignmentCount(resource, shift.laborRoleId, visibleShifts),
          )
        : 0;
      if (
        shift.siteLocationId
        && resource.homeLocationId === shift.siteLocationId
      ) {
        score += 15;
        reasons.push('Location match');
      }
      if (shift.laborRoleId && resource.rolePreferenceIds.includes(shift.laborRoleId)) {
        score += 20;
        reasons.push('Preferred role');
      }
      if (roleAssignmentCount > 0) {
        score += Math.min(18, roleAssignmentCount * 3);
        reasons.push(`${roleAssignmentCount} prior ${roleAssignmentCount === 1 ? 'assignment' : 'assignments'} in role`);
      }
      const hasAssignmentConflict = visibleShifts.some(other => {
        if (shift.id && other.id === shift.id) return false;
        const samePerson = other.assignments.some(assignment => assignmentMatchesResource(assignment, resource));
        return samePerson && windowsOverlap(shift.startsAt, shift.endsAt, other.startsAt, other.endsAt);
      });
      if (hasAssignmentConflict) {
        score -= 100;
        blocked = true;
        reasons.push('Schedule conflict');
      }
      const hasTimeOff = timeOffRequests.some(request => {
        const samePerson = resource.kind === 'employee'
          ? request.profileId === resource.personId
          : request.freelancerId === resource.personId;
        return samePerson && request.status === 'approved' && windowsOverlap(shift.startsAt, shift.endsAt, request.startsAt, request.endsAt);
      });
      if (hasTimeOff) {
        score -= 100;
        blocked = true;
        reasons.push('Approved time off');
      }
      if (!reasons.length) reasons.push('Available');
      return { resource, score, reasons, blocked, roleAssignmentCount };
    })
    .sort((a, b) => {
      if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
      return b.score - a.score || a.resource.title.localeCompare(b.resource.title);
    });
}

/** Destinations already authorized and hydrated by the schedule read. Never use the scheduling office as a fallback. */
export function shiftJobSites(shift: LaborScheduleShift): LaborScheduleJobSite[] {
  if (shift.installationJobId || shift.serviceTicketId) return [{
    id: shift.installationJobId || shift.serviceTicketId || shift.id,
    label: shift.jobDetails?.locationName || shift.eventName || 'Job site',
    locationName: shift.jobDetails?.locationName ?? null, venueId: shift.jobDetails?.venueId ?? null,
    address: shift.jobDetails?.address ?? null, addressParts: shift.jobDetails?.addressParts,
  }];
  if (shift.jobSites) return shift.jobSites;
  return shift.events.map(event => ({ id: event.id, label: event.venueName || event.name, locationName: event.venueName, venueId: event.venueId, address: event.venueAddress }));
}
